import { Backdrop, Button, DialogActions, MenuItem, Select } from '@mui/material';
import AppLoader from '../AppLoader/AppLoader';
import { MapContext, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from "highcharts/highcharts-more";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeederStatus } from '../../features/geoJson/feedersStatus';
import { updateColor } from "../../features/powerComplaints/colorSlice";
import alert from '../../images/Alert.png';
import blueCircle from '../../images/blueCircle.png';
import orangeCircle from '../../images/orange_Circle.png';
import InfoImage from "../../images/informationButton.png";
import poweron from '../../images/poweron.png';
import redGif from '../../images/red.gif';
import redCircle from '../../images/red_circle.png';
import triangleoff from '../../images/triangle-off.png';
import triangleon from '../../images/triangle-on.png';
import feild from '../../images/field.png';
import offline from '../../images/offline.png';

import { convertDatewithTime, getGoogleMapsApiKey, formatDate, subdivisionName } from '../../services/Functions';
import './map.scss';
import { fetchGeoJson } from '../../features/geoJson/geoJson';
import {
    clearComplaintHeatmapOverlay,
    createComplaintHeatmapOverlay,
    updateComplaintHeatmapOpacity,
} from '../../utils/complaintHeatmapOverlay';

HighchartsMore(Highcharts);

// Module-level cache: map instance persists across component mounts (saves API loads)
let cachedMapInstance = null;
let cachedMapDiv = null;
// Module-level arrays to track markers that need clearing on remount
let cachedFieldStaffMarkers = [];
let cachedComplaintMarkers = [];
let cachedHeatmapOverlay = null;
let cachedHeatmapInterval = null;
let cachedWeatherOverlays = [];
let hoverTooltipOverlay = null;

const containerStyle = {
    width: '100%',
    height: '100%'
};

const mapLibraries = ['geometry'];

const mapStyleFeederLight = [
    { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#fbfcfe' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 15 }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e3f2fd' }] },
];

const mapStyleFeederDark = [
    { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#f0f3f8' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
];

const FEEDER_LINE_PALETTE_LIGHT = [
    '#0D47A1', '#1B5E20', '#4A148C', '#006064', '#E65100',
    '#283593', '#B71C1C', '#004D40', '#4527A0', '#F57F17',
    '#1565C0', '#2E7D32', '#6A1B9A', '#00838F', '#AD1457',
];

const FEEDER_LINE_PALETTE_DARK = [
    '#1976D2', '#388E3C', '#7B1FA2', '#0097A7', '#F57C00',
    '#5C6BC0', '#E53935', '#00796B', '#673AB7', '#FFA000',
    '#42A5F5', '#66BB6A', '#AB47BC', '#26C6DA', '#EC407A',
];

/** Company / circle / division region polygons — lightMode true = Light toggle */
const REGION_POLYGON_PALETTE_LIGHT = [
    '#64B5F6', '#81C784', '#BA68C8', '#4DD0E1', '#FFB74D',
    '#7986CB', '#E57373', '#4DB6AC', '#9575CD', '#FFD54F',
    '#90CAF9', '#A5D6A7', '#CE93D8', '#F48FB1', '#BCAAA4',
];

const REGION_POLYGON_PALETTE_DARK = [
    '#5C9CE6', '#6FBF73', '#9B7BB8', '#4DB6AC', '#F5A962',
    '#7B8FD4', '#E57373', '#4DB6AC', '#9575CD', '#FFD54F',
    '#64B5F6', '#81C784', '#BA68C8', '#4DD0E1', '#FFB74D',
];

const mapStyleCompanyLight = [
    { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#eef2f6' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b3e5fc' }] },
];

const hashRegionPolygonPaletteIndex = (label, paletteLength) => {
    const s = String(label ?? '');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % paletteLength;
};

const getRegionPolygonFillColor = (feature, lightMode) => {
    const palette = lightMode ? REGION_POLYGON_PALETTE_LIGHT : REGION_POLYGON_PALETTE_DARK;
    const label = feature.getProperty?.('Name') || feature.getProperty?.('Circle') || '';
    return palette[hashRegionPolygonPaletteIndex(label, palette.length)];
};

const getFeederLineStrokeWeight = (mapInstance) => {
    const zoom = mapInstance?.getZoom?.() ?? 12;
    if (zoom >= 18) return 2;
    if (zoom >= 16) return 1.75;
    if (zoom >= 14) return 1.5;
    if (zoom >= 12) return 1.25;
    if (zoom >= 10) return 1.1;
    return 1;
};

const getFeederPolylineWeights = (mapInstance, isOff) => {
    const base = getFeederLineStrokeWeight(mapInstance);
    return {
        casing: base + 1.1,
        line: base + (isOff ? 0.4 : 0.15),
        hoverLine: base + (isOff ? 0.9 : 0.6),
    };
};

const hashFeederPaletteIndex = (feederCode, paletteLength) => {
    const s = String(feederCode ?? '');
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % paletteLength;
};

let hoveredFeederLineFeature = null;
const feederPolylineRegistry = new WeakMap();

const clearFeederLineHoverHighlight = (mapInstance) => {
    if (!mapInstance?.data || !hoveredFeederLineFeature) return;
    try {
        mapInstance.data.revertStyle(hoveredFeederLineFeature);
    } catch (e) {
        /* feature may have been removed */
    }
    hoveredFeederLineFeature = null;
};

const isFeederLineGeoFeature = (feature) => {
    const feederCode = feature?.properties?.['Feeder Code'];
    if (!feederCode || feederCode === '11kV') return false;
    if (feature?.properties?.SHAPE === 'Polygon') return false;
    const type = feature?.geometry?.type;
    return type === 'LineString' || type === 'MultiLineString';
};

const splitGeoJsonForFeederView = (geoJsonData) => {
    const polygons = [];
    const lines = [];
    if (!geoJsonData?.features?.length) {
        return { polygons, lines };
    }
    geoJsonData.features.forEach((feature) => {
        if (isFeederLineGeoFeature(feature)) {
            lines.push(feature);
        } else {
            polygons.push(feature);
        }
    });
    return { polygons, lines };
};

const coordsRingToLatLngPath = (ring) =>
    ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));

const extractPathsFromGeoFeature = (feature) => {
    const geom = feature?.geometry;
    if (!geom) return [];
    if (geom.type === 'LineString') {
        return [coordsRingToLatLngPath(geom.coordinates)];
    }
    if (geom.type === 'MultiLineString') {
        return geom.coordinates.map((ring) => coordsRingToLatLngPath(ring));
    }
    return [];
};

const resolveFeederStrokeColor = (feederCode, feederStatusList, lightMode) => {
    const palette = lightMode ? FEEDER_LINE_PALETTE_LIGHT : FEEDER_LINE_PALETTE_DARK;
    const feeders = normalizeFeederStatusList(feederStatusList);
    const codeNum = parseInt(feederCode, 10);
    const matching = feeders.find((f) => f.feeder_code == codeNum);

    if (matching && matching.current <= 3 && matching.current_status === 'OFF') {
        return { color: lightMode ? '#FF1744' : '#D50000', isOff: true };
    }
    return {
        color: palette[hashFeederPaletteIndex(feederCode, palette.length)],
        isOff: false,
    };
};

const clearFeederPolylines = (mapInstance) => {
    const entries = feederPolylineRegistry.get(mapInstance);
    if (!entries?.length) return;
    entries.forEach(({ casing, line, listeners }) => {
        listeners?.forEach((listener) => {
            window.google?.maps?.event?.removeListener(listener);
        });
        casing?.setMap(null);
        line?.setMap(null);
    });
    feederPolylineRegistry.delete(mapInstance);
};

const attachFeederPolylineHover = (polyline, mapInstance, feederCode, featureProps, feederStatusList) => {
    const listeners = [];
    const feeders = normalizeFeederStatusList(feederStatusList);

    listeners.push(
        polyline.addListener('mouseover', (event) => {
            const entry = feederPolylineRegistry
                .get(mapInstance)
                ?.find((e) => e.line === polyline);
            const weights = getFeederPolylineWeights(mapInstance, entry?.isOff);
            polyline.setOptions({
                strokeWeight: weights.hoverLine,
                zIndex: 220,
            });
            const codeNum = parseInt(feederCode, 10);
            const matchingFeeder = feeders.find((f) => f.feeder_code == codeNum);
            const featureName =
                featureProps?.Name ||
                featureProps?.name ||
                featureProps?.FEEDER;
            showHoverTooltip(
                event.latLng,
                buildFeederLineHoverHtml(feederCode, matchingFeeder, featureName),
                mapInstance,
            );
        }),
    );

    listeners.push(
        polyline.addListener('mouseout', () => {
            const entry = feederPolylineRegistry
                .get(mapInstance)
                ?.find((e) => e.line === polyline);
            if (entry) {
                const weights = getFeederPolylineWeights(mapInstance, entry.isOff);
                polyline.setOptions({
                    strokeWeight: weights.line,
                    zIndex: entry.isOff ? 130 : 110,
                });
            }
            clearHoverTooltip();
        }),
    );

    return listeners;
};

const syncFeederPolylines = (mapInstance, lineFeatures, { feederStatusList, lightMode }) => {
    if (!mapInstance || !window.google?.maps?.Polyline) return;
    clearFeederPolylines(mapInstance);

    if (!lineFeatures?.length) return;

    const casingColor = lightMode ? '#FFFFFF' : '#1A237E';
    const entries = [];

    lineFeatures.forEach((feature) => {
        const feederCode = feature.properties?.['Feeder Code'];
        const { color: strokeColor, isOff } = resolveFeederStrokeColor(
            feederCode,
            feederStatusList,
            lightMode,
        );
        const weights = getFeederPolylineWeights(mapInstance, isOff);
        const paths = extractPathsFromGeoFeature(feature);

        paths.forEach((path) => {
            if (!path || path.length < 2) return;

            const casing = new window.google.maps.Polyline({
                path,
                strokeColor: casingColor,
                strokeWeight: weights.casing,
                strokeOpacity: 0.9,
                zIndex: 88,
                map: mapInstance,
                clickable: false,
            });

            const line = new window.google.maps.Polyline({
                path,
                strokeColor,
                strokeWeight: weights.line,
                strokeOpacity: 1,
                zIndex: isOff ? 130 : 110,
                map: mapInstance,
                clickable: true,
            });

            const listeners = attachFeederPolylineHover(
                line,
                mapInstance,
                feederCode,
                feature.properties,
                feederStatusList,
            );

            entries.push({ casing, line, listeners, isOff });
        });
    });

    feederPolylineRegistry.set(mapInstance, entries);
};

const updateFeederPolylineStrokeWeights = (mapInstance) => {
    const entries = feederPolylineRegistry.get(mapInstance);
    if (!entries?.length) return;
    entries.forEach(({ casing, line, isOff }) => {
        const weights = getFeederPolylineWeights(mapInstance, isOff);
        casing?.setOptions({ strokeWeight: weights.casing });
        line?.setOptions({ strokeWeight: weights.line });
    });
};

const computeBoundsFromLineFeatures = (lineFeatures, extraPositions = []) => {
    if (!window.google?.maps?.LatLngBounds) return null;

    const bounds = new window.google.maps.LatLngBounds();
    let pointCount = 0;

    const extendCoord = (lat, lng) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        pointCount += 1;
    };

    lineFeatures?.forEach((feature) => {
        extractPathsFromGeoFeature(feature).forEach((path) => {
            path.forEach(({ lat, lng }) => extendCoord(lat, lng));
        });
    });

    extraPositions.forEach(({ lat, lng }) => extendCoord(lat, lng));

    return pointCount > 0 ? bounds : null;
};

const expandLatLngBounds = (bounds, ratio = 0.05) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latSpan = Math.max(ne.lat() - sw.lat(), 0.008);
    const lngSpan = Math.max(ne.lng() - sw.lng(), 0.008);
    const latPad = Math.max(latSpan * ratio, 0.002);
    const lngPad = Math.max(lngSpan * ratio, 0.002);

    return new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(sw.lat() - latPad, sw.lng() - lngPad),
        new window.google.maps.LatLng(ne.lat() + latPad, ne.lng() + lngPad),
    );
};

const DEFAULT_MAP_MIN_ZOOM = 4;

const clearMapBoundsRestriction = (mapInstance) => {
    if (!mapInstance) return;
    mapInstance.setOptions({
        restriction: null,
        minZoom: DEFAULT_MAP_MIN_ZOOM,
    });
};

const buildRegionBoundsFromExtents = (
    { minLat, maxLat, minLng, maxLng },
    marginRatio = 0.05,
) => {
    const latSpan = Math.max(maxLat - minLat, 0.01);
    const lngSpan = Math.max(maxLng - minLng, 0.01);
    const latPad = Math.max(latSpan * marginRatio, 0.002);
    const lngPad = Math.max(lngSpan * marginRatio, 0.002);

    return new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(minLat - latPad, minLng - lngPad),
        new window.google.maps.LatLng(maxLat + latPad, maxLng + lngPad),
    );
};

/** Fit company/circle/division polygons and prevent zooming out past that region. */
const fitMapToRegionView = (mapInstance, extents, options = {}) => {
    const {
        padding = 32,
        fitMarginRatio = 0.05,
        restrictionExpandRatio = 0.1,
        onLimitsApplied,
    } = options;

    if (!mapInstance || !extents || !window.google?.maps?.LatLngBounds) return null;

    const bounds = buildRegionBoundsFromExtents(extents, fitMarginRatio);
    mapInstance.fitBounds(bounds, padding);

    window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const zoomAfterFit = mapInstance.getZoom();
        if (zoomAfterFit == null) return;

        const restrictionBounds = expandLatLngBounds(bounds, restrictionExpandRatio);
        mapInstance.setOptions({
            minZoom: zoomAfterFit,
            restriction: {
                latLngBounds: restrictionBounds,
                strictBounds: false,
            },
        });

        onLimitsApplied?.({ minZoom: zoomAfterFit, restrictionBounds });
    });

    return bounds;
};

const applyRegionViewLimits = (mapInstance, regionState) => {
    if (!mapInstance || !regionState?.extents || regionState.minZoom == null) return;
    const bounds = buildRegionBoundsFromExtents(regionState.extents, 0.05);
    mapInstance.setOptions({
        minZoom: regionState.minZoom,
        restriction: {
            latLngBounds: expandLatLngBounds(bounds, 0.1),
            strictBounds: false,
        },
    });
};

const fitMapToFeederNetwork = (mapInstance, lineFeatures, options = {}) => {
    const { connections = [], padding = 32, restrictPan = true } = options;
    if (!mapInstance || !lineFeatures?.length) return null;

    const extraPositions = (connections || [])
        .filter((c) => c?.latitude != null && c?.longitude != null)
        .map((c) => ({
            lat: parseFloat(c.latitude),
            lng: parseFloat(c.longitude),
        }));

    const bounds = computeBoundsFromLineFeatures(lineFeatures, extraPositions);
    if (!bounds) return null;

    const fitBounds = expandLatLngBounds(bounds, 0.04);

    mapInstance.fitBounds(fitBounds, padding);


    if (restrictPan) {
        mapInstance.setOptions({
            restriction: {
                latLngBounds: expandLatLngBounds(fitBounds, 0.1),
                strictBounds: false,
            },
        });
    }

    return fitBounds;
};

const lastRegionClickRef = { time: 0, lat: null, lng: null };

/** Green marker = ON, red marker = OFF (contactor_status 0 = off). */
const getApmsContactorStatusLabel = (contactorStatus) =>
    contactorStatus === 0 ? 'OFF' : 'ON';

const isPointInRing = (latLng, ring) => {
    if (!ring?.length) return false;
    const x = latLng.lng();
    const y = latLng.lat();
    let inside = false;

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = typeof ring[i].lng === 'function' ? ring[i].lng() : ring[i][0];
        const yi = typeof ring[i].lat === 'function' ? ring[i].lat() : ring[i][1];
        const xj = typeof ring[j].lng === 'function' ? ring[j].lng() : ring[j][0];
        const yj = typeof ring[j].lat === 'function' ? ring[j].lat() : ring[j][1];

        const intersects = ((yi > y) !== (yj > y)) &&
            (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
        if (intersects) inside = !inside;
    }

    return inside;
};

const isPointInGeometry = (latLng, geometry) => {
    if (!geometry?.getType) return false;
    const geometryType = geometry.getType();

    if (geometryType === 'Polygon') {
        const paths = geometry.getArray();
        if (!paths?.length) return false;
        return isPointInRing(latLng, paths[0]);
    }

    if (geometryType === 'MultiPolygon') {
        return geometry.getArray().some((polygon) => isPointInGeometry(latLng, polygon));
    }

    if (geometryType === 'GeometryCollection') {
        return geometry.getArray().some((part) => isPointInGeometry(latLng, part));
    }

    return false;
};

const isPointInDataFeature = (latLng, feature) => {
    const geometry = feature?.getGeometry?.();
    if (!geometry) return false;

    if (window.google?.maps?.geometry?.poly) {
        const geometryType = geometry.getType?.();
        if (geometryType === 'Polygon') {
            return window.google.maps.geometry.poly.containsLocation(latLng, geometry);
        }
        if (geometryType === 'MultiPolygon') {
            return geometry.getArray().some(
                (polygon) => window.google.maps.geometry.poly.containsLocation(latLng, polygon)
            );
        }
    }

    return isPointInGeometry(latLng, geometry);
};

const DISCO_NAME_PATTERN = /\b(mepco|lesco|fesco|iesco|gepco|hesco|pesco|qesco|tesco|sepco|hazeco)\b/gi;
const REGION_LABEL_PATTERN = /\b(circle|division|motion|motionn|motionno|div|electric|power|company|supply)\b/gi;

const normalizeMapRegionName = (str) => {
    if (!str) return '';
    return String(str)
        .toLowerCase()
        .replace(DISCO_NAME_PATTERN, ' ')
        .replace(REGION_LABEL_PATTERN, ' ')
        .replace(/[\s.\-_]+/g, '')
        .trim();
};

const mapRegionNamesMatch = (a, b) => {
    const n1 = normalizeMapRegionName(a);
    const n2 = normalizeMapRegionName(b);
    if (!n1 || !n2) return false;
    if (n1 === n2) return true;
    const shorter = n1.length <= n2.length ? n1 : n2;
    const longer = n1.length > n2.length ? n1 : n2;
    return shorter.length >= 3 && longer.includes(shorter);
};

const divisionGeoJsonNameMatches = (featureName, selectedDivision) => {
    if (!selectedDivision) return false;
    const normalizedSelected = normalizeMapRegionName(selectedDivision);
    const normalizedFeature = normalizeMapRegionName(featureName);
    return (
        featureName === selectedDivision ||
        featureName === selectedDivision.trim() ||
        normalizedFeature === normalizedSelected ||
        mapRegionNamesMatch(featureName, selectedDivision)
    );
};

const createCustomOverlay = (position, content, map) => {
    if (!window.google?.maps) return null;

    const CustomOverlay = function () { };
    CustomOverlay.prototype = new window.google.maps.OverlayView();

    CustomOverlay.prototype.onAdd = function () {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.pointerEvents = 'none';
        div.style.zIndex = '1000';
        div.innerHTML = content;
        div.style.fontFamily = 'Arial, sans-serif';
        this.div = div;
        const panes = this.getPanes();
        panes.floatPane.appendChild(div);
    };

    CustomOverlay.prototype.draw = function () {
        const overlayProjection = this.getProjection();
        const pos = overlayProjection.fromLatLngToDivPixel(position);
        const div = this.div;
        if (div && pos) {
            div.style.left = pos.x + 'px';
            div.style.top = (pos.y - 30) + 'px';
        }
    };

    CustomOverlay.prototype.onRemove = function () {
        if (this.div?.parentNode) {
            this.div.parentNode.removeChild(this.div);
        }
        this.div = null;
    };

    const overlay = new CustomOverlay();
    overlay.setMap(map);
    return overlay;
};

const clearHoverTooltip = () => {
    if (!hoverTooltipOverlay) return;
    try {
        if (hoverTooltipOverlay.div?.parentNode) {
            hoverTooltipOverlay.div.parentNode.removeChild(hoverTooltipOverlay.div);
        }
        hoverTooltipOverlay.setMap(null);
    } catch (e) {
        /* overlay already removed */
    }
    hoverTooltipOverlay = null;
};

const clearMapHoverUi = (mapInstance) => {
    clearFeederLineHoverHighlight(mapInstance);
    clearHoverTooltip();
};

const showHoverTooltip = (position, content, mapInstance) => {
    if (mapInstance) clearFeederLineHoverHighlight(mapInstance);
    clearHoverTooltip();
    if (position && content && mapInstance) {
        hoverTooltipOverlay = createCustomOverlay(position, content, mapInstance);
    }
};

const attachMarkerHoverTooltip = (marker, content, mapInstance) => {
    if (!marker || !mapInstance) return;
    marker.addListener('mouseover', () => {
        showHoverTooltip(marker.getPosition(), content, mapInstance);
    });
    marker.addListener('mouseout', () => {
        clearHoverTooltip();
    });
};

const normalizeFeederStatusList = (feederArray) => {
    if (!feederArray?.length) return [];
    return feederArray.map((feeder) => ({
        feeder_code: parseInt(feeder.feeder_code, 10),
        feeder: feeder.feeder,
        current_status: feeder.current_status,
        current: feeder.current,
        grid: feeder.grid,
    }));
};

const buildFeederLineHoverHtml = (feederCode, matchingFeeder, featureName) => {
    if (matchingFeeder) {
        const offLabel =
            matchingFeeder.current_status === 'OFF' && matchingFeeder.current < 3
                ? 'OFF'
                : '';
        const ampsLabel =
            matchingFeeder.current_status === 'OFF' && matchingFeeder.current < 3
                ? ''
                : `${matchingFeeder.current} Amps`;
        return `
            <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; width: 220px;">
              <div style="display: flex; align-items: flex-start; gap: 8px;">
                <span style="flex: 1; font-size: 16px; font-weight: bold;">
                  ${feederCode || 'N/A'}
                </span>
                <span style="font-size: 16px; font-weight: bold; color: #f98126;">
                  ${offLabel}
                </span>
              </div>
              <p style="margin: 8px 0 4px; color:#fdff02;">${'11 KV ' + (matchingFeeder.feeder || 'N/A')}</p>
              <p style="margin: 4px 0;">${matchingFeeder.grid || 'N/A'}</p>
              <p style="color: #fdff02; margin: 4px 0 0;">${ampsLabel}</p>
            </div>`;
    }

    const title = featureName || `Feeder ${feederCode}`;
    return `
        <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; min-width: 180px;">
          <span style="font-size: 16px; font-weight: bold; color: #fdff02;">${title}</span>
          <p style="margin: 8px 0 0;"><strong>Feeder Code:</strong> ${feederCode || 'N/A'}</p>
        </div>`;
};

const attachFeederLineDataHoverListeners = (mapInstance, feederStatusList) => {
    if (!mapInstance?.data || !window.google?.maps?.event) return;

    clearFeederLineHoverHighlight(mapInstance);
    window.google.maps.event.clearListeners(mapInstance.data, 'mouseover');
    window.google.maps.event.clearListeners(mapInstance.data, 'mouseout');

    const feeders = normalizeFeederStatusList(feederStatusList);

    mapInstance.data.addListener('mouseover', (event) => {
        const feederCode = event.feature.getProperty('Feeder Code');
        const shapeType = event.feature.getProperty('SHAPE');
        if (!feederCode || shapeType === 'Polygon') return;

        if (hoveredFeederLineFeature && hoveredFeederLineFeature !== event.feature) {
            clearFeederLineHoverHighlight(mapInstance);
        }

        hoveredFeederLineFeature = event.feature;
        const baseWeight = getFeederLineStrokeWeight(mapInstance);
        mapInstance.data.overrideStyle(event.feature, {
            strokeWeight: baseWeight + 3,
            strokeOpacity: 1,
            zIndex: 250,
        });

        const codeNum = parseInt(feederCode, 10);
        const matchingFeeder = feeders.find((f) => f.feeder_code == codeNum);
        const featureName =
            event.feature.getProperty('Name') ||
            event.feature.getProperty('name') ||
            event.feature.getProperty('FEEDER');

        const contentString = buildFeederLineHoverHtml(
            feederCode,
            matchingFeeder,
            featureName,
        );
        showHoverTooltip(event.latLng, contentString, mapInstance);
    });

    mapInstance.data.addListener('mouseout', (event) => {
        const feederCode = event.feature.getProperty('Feeder Code');
        const shapeType = event.feature.getProperty('SHAPE');
        if (feederCode && shapeType !== 'Polygon') {
            clearFeederLineHoverHighlight(mapInstance);
            clearHoverTooltip();
        }
    });
};

const clearDataLayerListeners = (mapInstance) => {
    clearMapHoverUi(mapInstance);
    if (mapInstance) clearFeederPolylines(mapInstance);
    if (!mapInstance?.data || !window.google?.maps?.event) return;
    ['click', 'mouseover', 'mouseout'].forEach((eventName) => {
        window.google.maps.event.clearListeners(mapInstance.data, eventName);
    });
};

const ensureRegionClickListener = (mapInstance, clickHandler) => {
    attachMapRegionClickHandler(mapInstance, clickHandler);
};

const findDataFeatureAtLatLng = (mapInstance, latLng) => {
    if (!mapInstance?.data || !latLng) return null;

    let matchedFeature = null;
    mapInstance.data.forEach((feature) => {
        if (matchedFeature) return;

        const feederCode = feature.getProperty('Feeder Code');
        if (feederCode) return;

        if (isPointInDataFeature(latLng, feature)) {
            matchedFeature = feature;
        }
    });

    return matchedFeature;
};

const invokeRegionClick = (clickHandler, payload) => {
    const now = Date.now();
    const lat = payload.latLng?.lat?.();
    const lng = payload.latLng?.lng?.();

    if (
        lat != null &&
        lng != null &&
        now - lastRegionClickRef.time < 300 &&
        lastRegionClickRef.lat === lat &&
        lastRegionClickRef.lng === lng
    ) {
        return;
    }

    lastRegionClickRef.time = now;
    lastRegionClickRef.lat = lat ?? null;
    lastRegionClickRef.lng = lng ?? null;
    clickHandler(payload);
};

const attachMapRegionClickHandler = (mapInstance, clickHandler) => {
    if (!mapInstance || !clickHandler || !window.google?.maps?.event) return;

    window.google.maps.event.clearListeners(mapInstance.data, 'click');

    if (mapInstance._regionMapClickListener) {
        window.google.maps.event.removeListener(mapInstance._regionMapClickListener);
        mapInstance._regionMapClickListener = null;
    }

    // Direct polygon click
    mapInstance.data.addListener('click', (event) => {
        if (!event?.feature) return;
        invokeRegionClick(clickHandler, { feature: event.feature, latLng: event.latLng });
        clearHoverTooltip();
    });

    // Fallback when markers/heatmap sit above polygons
    mapInstance._regionMapClickListener = mapInstance.addListener('click', (event) => {
        const feature = findDataFeatureAtLatLng(mapInstance, event.latLng);
        if (!feature) return;
        invokeRegionClick(clickHandler, { feature, latLng: event.latLng });
        clearHoverTooltip();
    });
};

const attachMapHoverCleanup = (mapInstance) => {
    if (!mapInstance?.getDiv) return;
    const mapDiv = mapInstance.getDiv();
    if (mapDiv.dataset.hoverCleanupAttached === 'true') return;
    mapDiv.dataset.hoverCleanupAttached = 'true';
    mapDiv.addEventListener('mouseleave', () => clearMapHoverUi(mapInstance));
    mapInstance.addListener('dragstart', () => clearMapHoverUi(mapInstance));
    mapInstance.addListener('zoom_changed', () => {
        clearMapHoverUi(mapInstance);
        updateFeederPolylineStrokeWeights(mapInstance);
    });
};

function Map({ connections, fieldStaff, complaints, handleSubmit, onComplaintsUpdate, selectedMarker1, updateSelectedMarker1, company, setCode, setDivision, setSubDivision, hierarchyCode, selectedCompanyCode, circleCode, divisionCode, weatherData, parentData, onRegionClick }) {
    const subDivisions = JSON.parse(window.localStorage.getItem('subDivisions'));
    const dispatch = useDispatch();
    const { color } = useSelector((state) => state.feederColor);
    const { home: homeButtonState } = useSelector(state => state.homeButton);
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const { code, hierarchy, token, permissions, name, roleName, userId, circleCode: CircleCode, roleTypeId } = useSelector((state) => state.auth);
    const { data: geoJSON } = useSelector((state) => state.geoJson);
    const { data: divisionGeoJson } = useSelector((state) => state.divisionGeoJson);
    const { officeCode } = useSelector(state => state.officeCode);

    const [map, setMap] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [apmsMarkers, setApmsMarkers] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState();
    const [avgLat, setAvgLat] = useState();
    const [avgLng, setAvgLng] = useState();
    const [flash, setFlash] = useState(true);
    const [center, setCenter] = useState({
        lat: avgLat,
        lng: avgLng
    });
    const [feederInfo, setFeederInfo] = useState([]);
    const [verificationPopup, setVerificationPopup] = useState(false);
    const [verificationPopup1, setVerificationPopup1] = useState(false);
    const [data1, setData1] = useState();
    const [showFeedbackDropdown, setShowFeedbackDropdown] = useState(false);
    const [reason, setReason] = useState("1");
    const [isBoundsSet, setIsBoundsSet] = useState();
    const [displayMode, setDisplayMode] = useState('lineman'); // Toggle: 'lineman' | 'complaints' | 'both' | 'heatmap' | 'weather'
    const [selectedDesignationFilter, setSelectedDesignationFilter] = useState('all'); // 'all' or specific designation like 'ALM', 'LM-I', etc.
    const [complaintMarkers, setComplaintMarkers] = useState([]); // Track complaint markers for clearing
    const heatmapOverlayRef = useRef(null);
    const heatmapBlinkRef = useRef(null);
    const weatherOverlaysRef = useRef([]);
    const displayModeRef = useRef(displayMode);
    const mapContainerRef = useRef(null);
    const feederLineFeaturesRef = useRef([]);
    const regionViewStateRef = useRef(null);

    const mountComplaintHeatmap = (targetMap, complaints, animate = false) => {
        const overlay = createComplaintHeatmapOverlay(targetMap, complaints, 1);
        if (!overlay) return;

        heatmapOverlayRef.current = overlay;
        cachedHeatmapOverlay = overlay;

        if (animate) {
            let opacity = 1;
            let direction = -1;
            const interval = setInterval(() => {
                if (!heatmapOverlayRef.current) return;

                opacity += direction * 0.03;
                if (opacity <= 0.35) {
                    opacity = 0.35;
                    direction = 1;
                } else if (opacity >= 1) {
                    opacity = 1;
                    direction = -1;
                }

                updateComplaintHeatmapOpacity(heatmapOverlayRef.current, opacity);
            }, 40);
            heatmapBlinkRef.current = interval;
            cachedHeatmapInterval = interval;
        }
    };


    const [loadOption, setLoadOption] = useState({
        chart: {
            type: 'gauge',
            height: '100%',
            backgroundColor: 'transparent',
            width: null,
        },
        title: {
            text: 'Load (kVA)',
            style: {
                fontSize: '15px'
            }
        },
        legend: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ['50%', '75%'],
            size: '110%'
        },
        yAxis: {
            min: 0,
            max: 100,
            tickPixelInterval: 72,
            tickPosition: 'inside',
            tickColor: Highcharts.defaultOptions.chart.backgroundColor || '#FFFFFF',
            tickLength: 10,
            tickWidth: 2,
            minorTickInterval: null,
            labels: {
                distance: -25,
                enabled: false
            },
            lineWidth: 0,
            plotBands: [{
                from: 0,
                to: 25,
                color: '#05b129',
                thickness: 30,
                label: {
                    text: 'Low',
                    align: 'center',
                    style: {
                        color: '#FFFFFF',
                        fontSize: '10px',
                        fontWeight: 'bold'
                    },
                    x: 30,
                    y: 20,
                    rotation: 0
                }
            }, {
                from: 25,
                to: 50,
                color: '#55BF3B',
                thickness: 30,
                label: {
                    text: 'Medium',
                    align: 'center',
                    style: {
                        color: '#FFFFFF',
                        fontSize: '10px',
                        fontWeight: 'bold'
                    },
                    x: 40,
                    y: 30,
                    rotation: 0
                }
            }, {
                from: 50,
                to: 75,
                color: '#f69d26',
                thickness: 30,
                label: {
                    text: 'High',
                    align: 'center',
                    style: {
                        color: '#FFFFFF',
                        fontSize: '10px',
                        fontWeight: 'bold'
                    },
                    x: -30,
                    y: 30,
                    rotation: 0
                }
            }, {
                from: 75,
                to: 100,
                color: '#ef4b31',
                thickness: 30,
                label: {
                    text: 'Max',
                    align: 'center',
                    style: {
                        color: '#FF0000',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    },
                    x: -30,
                    y: 20,
                    rotation: 0
                }
            }]
        },
        series: [{
            name: 'Load',
            tooltip: {
                valueSuffix: ''
            },
            dataLabels: {
                enabled: false,
                format: '{y}',
                borderWidth: 0,
                color: (
                    Highcharts.defaultOptions.title &&
                    Highcharts.defaultOptions.title.style &&
                    Highcharts.defaultOptions.title.style.color
                ) || '#333333',
                style: {
                    fontSize: '16px'
                }
            },
            dial: {
                radius: '80%',
                backgroundColor: 'gray',
                baseWidth: 12,
                baseLength: '0%',
                rearLength: '0%'
            },
            pivot: {
                backgroundColor: 'gray',
                radius: 6
            }
        }]
    });

    const [voltageOptions, setVoltageOptions] = useState({
        chart: {
            type: 'bar',
            height: 150,
            backgroundColor: 'transparent'
        },
        title: {
            text: 'Voltage',
            style: {
                fontSize: '15px',
                margin: 0
            }
        },
        xAxis: {
            categories: ['Va', 'Vb', 'Vc'],
        },
        legend: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        yAxis: {
            min: 0,
            title: {
                text: '',
            },
        },
        plotOptions: {
            series: {
                pointWidth: 15,
                pointPadding: 0.05,
                groupPadding: 0.1,
            },
        },
        series: [
            {
                name: 'Voltage',
                data: [232, 229, 229],
                colorByPoint: true,
                colors: ['#E46E6E', '#E4D354', '#7CB5EC'],
            },
        ],
    });

    const [currentOptions, setCurrentOptions] = useState({
        chart: {
            type: 'bar',
            height: 150,
            backgroundColor: 'transparent'
        },
        title: {
            text: 'Current (Amp)',
            style: {
                fontSize: '15px'
            }
        },
        xAxis: {
            categories: ['Ia', 'Ib', 'Ic'],
        },
        yAxis: {
            min: 0,
            title: {
                text: '',
            },
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                pointWidth: 15,
                pointPadding: 0.05,
                groupPadding: 0.1,
            },
        },
        legend: {
            enabled: false,
        },
        series: [
            {
                name: 'Current',
                data: [50, 39, 20],
                colorByPoint: true,
                colors: ['#E46E6E', '#E4D354', '#7CB5EC'],
            },
        ],
    });

    useEffect(() => {
        setSelectedMarker(selectedMarker1);
    }, [selectedMarker1]);

    const slicedCode = code.toString().slice(0, 2);
    const stableApiKey = useRef(getGoogleMapsApiKey(slicedCode)).current;
    const companyCode = code.slice(0, 2);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script-v2',
        googleMapsApiKey: stableApiKey,
        libraries: mapLibraries,
    });

    // Cached map initialization: creates map only once, reuses on remount
    useEffect(() => {
        if (!isLoaded || !mapContainerRef.current) return;

        if (cachedMapInstance && cachedMapDiv) {
            // Reuse existing map - move its div back into our container
            mapContainerRef.current.innerHTML = '';
            mapContainerRef.current.appendChild(cachedMapDiv);
            mapRef.current = cachedMapInstance;

            // Clear all cached markers when remounting (so displayMode 'lineman' default works)
            if (cachedFieldStaffMarkers && cachedFieldStaffMarkers.length > 0) {
                cachedFieldStaffMarkers.forEach(marker => {
                    try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                });
                cachedFieldStaffMarkers = [];
            }
            if (cachedComplaintMarkers && cachedComplaintMarkers.length > 0) {
                cachedComplaintMarkers.forEach(marker => {
                    try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                });
                cachedComplaintMarkers = [];
            }
            if (cachedHeatmapOverlay) {
                clearComplaintHeatmapOverlay(cachedHeatmapOverlay);
                cachedHeatmapOverlay = null;
            }
            if (cachedHeatmapInterval) {
                clearInterval(cachedHeatmapInterval);
                cachedHeatmapInterval = null;
            }

            attachMapHoverCleanup(cachedMapInstance);
            setMap(cachedMapInstance);
            // Re-trigger the appropriate mapLoad for current hierarchy
            const m = cachedMapInstance;
            (hierarchy == 52) ? mapLoadSubdivision(m) : (hierarchy == 2 || hierarchy == 1) ? mapLoadCompany(m) : (hierarchy == 3) ? mapLoadCircle(m) : (hierarchy == 4 && divisionGeoJson) ? mapLoadDivision(m) : divisionGeoJson && mapLoadDivision(m);
        } else {
            // First time: create the map and cache it
            const div = document.createElement('div');
            div.style.width = '100%';
            div.style.height = '100%';
            mapContainerRef.current.appendChild(div);

            const newMap = new window.google.maps.Map(div, {
                zoomControl: false,
                streetViewControl: false,
                minZoom: 4,
            });
            cachedMapInstance = newMap;
            cachedMapDiv = div;
            mapRef.current = newMap;
            attachMapHoverCleanup(newMap);
            setMap(newMap);
            // Call mapLoad for the first time
            (hierarchy == 52) ? mapLoadSubdivision(newMap) : (hierarchy == 2 || hierarchy == 1) ? mapLoadCompany(newMap) : (hierarchy == 3) ? mapLoadCircle(newMap) : (hierarchy == 4 && divisionGeoJson) ? mapLoadDivision(newMap) : divisionGeoJson && mapLoadDivision(newMap);
        }

        return () => {
            // On unmount: detach the map div but keep it alive
            if (cachedMapDiv && cachedMapDiv.parentNode) {
                cachedMapDiv.parentNode.removeChild(cachedMapDiv);
            }
            setMap(null);
        };
    }, [isLoaded]);

    const { data: result1 } = useSelector((state) => state.feederStatus);

    useEffect(() => {
        let coordinates = [];
        if (complaints.length > 0) {
            coordinates = [...coordinates, complaints.map(item => {
                return {
                    latitude: item.Latitude,
                    longitude: item.Longitude
                }
            })];
        }
    }, [complaints]);

    useEffect(() => {
        setSelectedMarker('');
    }, [complaints]);

    useEffect(() => {
        if (selectedMarker) {
            if (selectedMarker.kva) {
                const apmsCapacity = parseFloat(selectedMarker.apms_capacity) || 100;
                // const currentLoad = Math.abs(parseFloat(selectedMarker.kva));
                const currentLoad = parseFloat(selectedMarker.kva) || 0;
                // Calculate dynamic ranges based on APMS capacity
                const low = apmsCapacity * 0.25;
                const medium = apmsCapacity * 0.50;
                const high = apmsCapacity * 0.75;
                const max = apmsCapacity;


                setLoadOption(prevOptions => ({
                    ...prevOptions,
                    yAxis: {
                        ...prevOptions.yAxis,
                        min: 0,
                        max: max,
                        plotBands: [{
                            from: 0,
                            to: low,
                            color: '#05b129',
                            thickness: 30,
                            label: {
                                text: 'Low',
                                align: 'center',
                                style: {
                                    color: '#FFFFFF',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                },
                                x: 30,
                                y: 20,
                                rotation: 0
                            }
                        }, {
                            from: low,
                            to: medium,
                            color: '#55BF3B',
                            thickness: 30,
                            label: {
                                text: 'Medium',
                                align: 'center',
                                style: {
                                    color: '#FFFFFF',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                },
                                x: 40,
                                y: 30,
                                rotation: 0
                            }
                        }, {
                            from: medium,
                            to: high,
                            color: '#f69d26',
                            thickness: 30,
                            label: {
                                text: 'High',
                                align: 'center',
                                style: {
                                    color: '#FFFFFF',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                },
                                x: -30,
                                y: 30,
                                rotation: 0
                            }
                        }, {
                            from: high,
                            to: max,
                            color: '#ef4b31',
                            thickness: 30,
                            label: {
                                text: 'Max',
                                align: 'center',
                                style: {
                                    color: '#FFFFFF',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                },
                                x: -30,
                                y: 20,
                                rotation: 0
                            }
                        }]
                    },
                    series: [{
                        ...prevOptions.series[0],
                        data: [currentLoad]
                    }]
                }));
                setVoltageOptions(prevOptions => ({
                    ...prevOptions,
                    series: [{
                        ...prevOptions.series[0],
                        data: [parseFloat(selectedMarker.v1), parseFloat(selectedMarker.v2), parseFloat(selectedMarker.v3)]
                    }]
                }));
                setCurrentOptions(prevOptions => ({
                    ...prevOptions,
                    series: [{
                        ...prevOptions.series[0],
                        data: [Math.abs(parseFloat(selectedMarker.i1)),
                        Math.abs(parseFloat(selectedMarker.i2)),
                        Math.abs(parseFloat(selectedMarker.i3))]
                    }]
                }));
            }
        }
    }, [selectedMarker]);

    const filteredFieldStaff = useMemo(() => {
        if (!fieldStaff || fieldStaff.length === 0) {
            return [];
        }

        // Get current hour and decide shift
        const now = new Date();
        const currentHour = now.getHours();

        let currentShiftId;
        if (currentHour >= 6 && currentHour < 14) {
            currentShiftId = "1";
        } else if (currentHour >= 14 && currentHour < 22) {
            currentShiftId = "2";
        } else {
            currentShiftId = "3";
        }

        // Filter by shift - handle both shiftId (number) and shiftIds (comma-separated string)
        let result = fieldStaff.filter(item => {
            // If shiftIds exists as comma-separated string (e.g., "1,2,3")
            if (item.shiftIds) {
                const shiftIdsArray = item.shiftIds.toString().replace(/\s+/g, "").split(",");
                return shiftIdsArray.includes(currentShiftId);
            }
            // If shiftId exists as single value
            if (item.shiftId !== undefined) {
                return item.shiftId?.toString() === currentShiftId;
            }
            return true;
        });

        result = result.filter(item => {
            const designation = item.designationName?.toString().toUpperCase();
            return designation?.includes("ALM") ||
                designation?.includes("LM-I")   ||
                designation?.includes("LS-II")  ||
                designation?.includes("LM-II")  ||
                designation?.includes("LS-I")
        });

        // Filter out inactive linemen (not logged in)
        result = result.filter(item => item.isLogged_In === true || item.isLogged_In === 1);

        if (hierarchy == 1 && homeButtonState && selectedCompanyCode) {
            result = result.filter((item) => {
                const subDiv = item.subDivisionCode?.toString() || '';
                const circle = item.circleCode?.toString() || '';
                return (
                    subDiv.startsWith(String(selectedCompanyCode)) ||
                    circle.startsWith(String(selectedCompanyCode))
                );
            });
        }

        if (circleCode && homeButtonState === false) {
            result = result.filter(item => item.circleCode?.toString() == circleCode?.toString());
        }
        if (divisionCode && homeButtonState === false) {
            result = result.filter(item => item.divisionCode?.toString() == divisionCode?.toString());
        }
        // Filter by subdivision when selected (for hierarchy 1,2,3,4)
        if (setSubDivision && homeButtonState === false) {
            result = result.filter(item => {
                // Handle different subdivision code formats - compare first 5 digits
                const itemSubDiv = item.subDivisionCode?.toString()?.slice(0, 5);
                const selectedSubDiv = setSubDivision?.toString()?.slice(0, 5);
                return itemSubDiv == selectedSubDiv;
            });
        }

        if (selectedDesignationFilter && selectedDesignationFilter !== 'all') {
            result = result.filter(item => {
                const designation = item.designationName?.toString().toUpperCase();
                return designation?.includes(selectedDesignationFilter.toUpperCase());
            });
        }

        return result;
    }, [fieldStaff, circleCode, divisionCode, setSubDivision, homeButtonState, selectedDesignationFilter, hierarchy, selectedCompanyCode]);

    const fieldStaffMarkerKey = useMemo(() => {
        if (!filteredFieldStaff?.length) return '';
        return filteredFieldStaff
            .map((item) =>
                [
                    item.employeeCode || item.userId || item.id || '',
                    item.latitude,
                    item.longitude,
                    item.isLogged_In,
                ].join(':')
            )
            .sort()
            .join('|');
    }, [filteredFieldStaff]);

    // Filter complaints based on drilldown level using RefNo
    // RefNo structure: 07111120508304
    // Position 3-4 (index 2-4): Company code (11)
    // Position 3-5 (index 2-5): Circle code (111)
    // Position 3-6 (index 2-6): Division code (1111)
    const filteredComplaints = useMemo(() => {
        if (!complaints || complaints.length === 0) return [];

        let result = [...complaints];

        // Own-level users: show all circle/division/subdivision complaints until map drill-down filters apply
        if (
            hierarchy == 52 ||
            ((hierarchy == 1 || hierarchy == 2 || hierarchy == 3 || hierarchy == 4) && !circleCode && !divisionCode && !setSubDivision)
        ) {
            return result;
        }

        // Filter by circle code
        if (circleCode && homeButtonState === false) {
            result = result.filter(item => {
                const refNoCircle = item.RefNo?.substring(2, 5);
                return refNoCircle === circleCode?.toString();
            });
        }

        // Filter by division code
        if (divisionCode && homeButtonState === false) {
            result = result.filter(item => {
                const refNoDivision = item.RefNo?.substring(2, 6);
                return refNoDivision === divisionCode?.toString();
            });
        }

        // Filter by subdivision when selected
        if (setSubDivision && homeButtonState === false) {
            result = result.filter(item => {
                const refNoSubDiv = item.RefNo?.substring(2, 7);
                return refNoSubDiv === setSubDivision?.toString()?.slice(0, 5);
            });
        }

        return result;
    }, [complaints, circleCode, divisionCode, setSubDivision, homeButtonState, hierarchy]);

    const getAPMSCoordinates = (connections) => {
        // Clear existing APMS markers first (and any hover tooltip they opened)
        clearHoverTooltip();
        apmsMarkers.forEach(marker => marker.setMap(null));

        // Show APMS when user is subdivision level OR when drilling down to subdivision
        if ((hierarchy == 52 || setSubDivision) && connections && connections.length > 0) {
            const newApmsMarkers = [];
            connections.forEach(connection => {
                if (connection.latitude && connection.longitude) {
                    const marker = new window.google.maps.Marker({
                        position: { lat: parseFloat(connection.latitude), lng: parseFloat(connection.longitude) },
                        icon: {
                            url: (connection.contactor_status === 0) ? triangleoff : triangleon,
                            size: new window.google.maps.Size(28, 28),
                            scaledSize: new window.google.maps.Size(28, 28)
                        },
                        map,
                        title: connection.id,
                        zIndex: 100
                    });

                    const contentString = `
                    <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; z-index=2000 ">
                        <div >
                            <span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;">
                                ${connection.name || 'N/A'}
                            </span>
                        </div>
                        <br />
                        <p style="margin: 5px 0;"><strong>MSN:</strong> ${connection.msn || 'N/A'}</p>
                        <p style="margin: 5px 0; color: #fdff02;"><strong>Load:</strong> ${connection.kva} kVA</p>
                        <p style="margin: 5px 0;"><strong>Last Comm:</strong> ${connection.last_communication_time || 'N/A'}</p>
                        <p style="margin: 5px 0;"><strong>Contactor status:</strong> <span style="color: ${connection.contactor_status === 0 ? '#e74c3c' : '#2ecc71'}; font-weight: bold;">${getApmsContactorStatusLabel(connection.contactor_status)}</span></p>
                    </div>`;

                    attachMarkerHoverTooltip(marker, contentString, map);
                    marker.addListener('click', () => {
                        clearHoverTooltip();
                        setSelectedMarker(connection);
                    });

                    newApmsMarkers.push(marker);
                }
            });
            setApmsMarkers(newApmsMarkers);
        } else {
            setApmsMarkers([]);
        }
    };

    const getComplaintLatLng = (complaint) => {
        if (!complaint) return null;
        const lat = parseFloat(
            complaint.Latitude ?? complaint.latitude ?? complaint.Lat ?? complaint.lat
        );
        const lng = parseFloat(
            complaint.Longitude ??
                complaint.longitude ??
                complaint.Long ??
                complaint.lng ??
                complaint.Lng
        );
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        if (lat === 0 && lng === 0) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return { lat, lng };
    };

    const getComplaintStatus = (complaint) =>
        complaint?.Status ?? complaint?.status ?? '';

    const getComplaintsCoordinates = (complaints, clearLinemanMarkers = true) => {
        clearHoverTooltip();
        // Clear existing complaint markers first
        if (complaintMarkers && complaintMarkers.length > 0) {
            complaintMarkers.forEach(marker => {
                try {
                    if (marker && marker.getMap()) marker.setMap(null);
                } catch (e) { /* marker already removed */ }
            });
            cachedComplaintMarkers = [];
        }
        // Clear lineman markers only if not in 'both' mode
        if (clearLinemanMarkers && markers && markers.length > 0) {
            markers.forEach(marker => {
                try {
                    if (marker && marker.getMap()) marker.setMap(null);
                } catch (e) { /* marker already removed */ }
            });
            setMarkers([]);
            cachedFieldStaffMarkers = [];
        }

        if (map && complaints && complaints.length > 0) {
            const newComplaintMarkers = [];
            complaints.forEach(complaint => {
                const coords = getComplaintLatLng(complaint);
                const status = getComplaintStatus(complaint);
                // Pending = red, Allocated = blue (same as list legend)
                if (coords && (status === 'Pending' || status === 'Allocated')) {
                    const marker = new window.google.maps.Marker({
                        position: coords,
                        icon: {
                            url: status === 'Allocated' ? blueCircle : redCircle,
                            size: new window.google.maps.Size(14, 14),
                            scaledSize: new window.google.maps.Size(14, 14)
                        },
                        map,
                        zIndex: 50,
                    });

                    const contentString = `
                    <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; width: 200px;">
                      <div style="display: flex;">
                        <span style="flex-grow: 1; font-size: 14px; font-weight: bold;">
                          ${complaint.RefNo || 'N/A'}
                        </span>
                      </div>
                      <p style="margin: 5px 0; color: #fdff02">${complaint.Name || 'N/A'}</p>
                      <p style="margin: 5px 0;"> ${complaint.NearByFamousPlace || complaint.Address || 'N/A'}</p>
                      ${complaint.userInfo ?
                            `<p style="margin: 5px 0;color: #fdff02">${complaint.userInfo.userFullName || 'N/A'} ${complaint.userInfo.userId ?
                                (complaint.userInfo.userId.startsWith('Lm') ? ' - Lineman' :
                                    complaint.userInfo.userId.startsWith('Alm') ? 'Asis Lineman' : complaint.userInfo.userId) :
                                'N/A'}</p>` : ''}
                    </div>`;

                    attachMarkerHoverTooltip(marker, contentString, map);

                    marker.addListener('click', () => {
                        clearHoverTooltip();
                        onComplaintsUpdate(complaint);
                        handleSubmit(complaint);
                    });

                    newComplaintMarkers.push(marker);
                }
            });
            setComplaintMarkers(newComplaintMarkers);
            // Also update cached markers for clearing on remount
            cachedComplaintMarkers = newComplaintMarkers;
        } else {
            setComplaintMarkers([]);
            cachedComplaintMarkers = [];
        }
    };

    const getFieldStaffCoordinates = (filteredFieldStaff, clearComplaintMarkers = true) => {
        clearHoverTooltip();
        // Clear existing lineman markers from React state and module cache
        const existingStaff =
            (markers && markers.length > 0) ? markers : cachedFieldStaffMarkers;
        if (existingStaff && existingStaff.length > 0) {
            existingStaff.forEach(marker => {
                try {
                    if (marker && marker.getMap()) marker.setMap(null);
                } catch (e) { /* marker already removed */ }
            });
            setMarkers([]);
            cachedFieldStaffMarkers = [];
        }
        // Clear complaint markers only if not in 'both' mode
        if (clearComplaintMarkers && complaintMarkers && complaintMarkers.length > 0) {
            complaintMarkers.forEach(marker => {
                try {
                    if (marker && marker.getMap()) marker.setMap(null);
                } catch (e) { /* marker already removed */ }
            });
            setComplaintMarkers([]);
            cachedComplaintMarkers = [];
        }

        if (map && filteredFieldStaff && filteredFieldStaff.length > 0) {
            const newMarkers = filteredFieldStaff.map(connection => {
                if (connection.latitude && connection.longitude) {
                    const marker = new window.google.maps.Marker({
                        position: { lat: parseFloat(connection.latitude), lng: parseFloat(connection.longitude) },
                        icon: {
                            url: connection.isLogged_In == false ? offline : feild,
                            size: new window.google.maps.Size(60, 60),
                            scaledSize: new window.google.maps.Size(60, 60)
                        },
                        map,
                        title: connection.id,
                        zIndex: 10,
                        opacity: 1,
                        clickable: false,
                    });

                    const contentString = `
                    <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; z-index: 2000">
                        <div><span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;"><strong>Name:</strong> ${connection.employeeName || 'N/A'}</span></div>
                        <div><span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;"><strong>Designation:</strong> ${connection.designationName || 'N/A'}</span></div>
                        <div><span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;"><strong>Last Logged in Time:</strong> ${formatDate(connection.loggedIn_time) || 'N/A'}</span></div>
                        <div><span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;"><strong>Status:</strong> ${(connection.isLogged_In == false || connection.isLogged_In == null) ? 'Inactive' : 'Active'}</span></div>
                        <div><span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;"><strong>Sub Division:</strong> ${subdivisionName(parseInt(connection.subDivisionCode), subDivisions)}</span></div>
                    </div>`;

                    attachMarkerHoverTooltip(marker, contentString, map);

                    return marker;
                }
                return null;
            }).filter(marker => marker !== null);

            setMarkers(newMarkers);
            // Also update cached markers for clearing on remount
            cachedFieldStaffMarkers = newMarkers;
        }
    };


    // Weather overlay: color polygons by weather and show info labels
    // Helper: find matching parentData circle for a GeoJSON feature
    const findMatchingCircle = (feature) => {
        if (!parentData || parentData.length === 0) return null;
        const featureName = feature.getProperty ? feature.getProperty('Name') || '' : (feature.Name || '');
        const featureCircle = feature.getProperty ? feature.getProperty('Circle') || '' : (feature.Circle || '');
        const featureCircleCode = feature.getProperty ? feature.getProperty('CircleCode') || '' : (feature.CircleCode || '');

        return parentData.find(p => {
            const pName = (p.name || '').toLowerCase().trim();
            const pCode = (p.Code || '').toString();
            const fName = featureName.toLowerCase().trim();
            const fCircle = featureCircle.toLowerCase().trim();
            const pNorm = pName.replace(/[\s.]+/g, '');
            const fNorm = fName.replace(/[\s.]+/g, '');
            const fCircleNorm = fCircle.replace(/[\s.]+/g, '');
            if (featureCircleCode && pCode && featureCircleCode.toString() === pCode.slice(0, 3)) return true;
            if (fCircle && (pName === fCircle || fCircle.includes(pName) || pName.includes(fCircle) || pNorm === fCircleNorm || fCircleNorm.includes(pNorm) || pNorm.includes(fCircleNorm))) return true;
            if (fName && (pName === fName || fName.includes(pName) || pName.includes(fName) || pNorm === fNorm || fNorm.includes(pNorm) || pNorm.includes(fNorm))) return true;
            return false;
        });
    };

    // Helper: get weather fill/stroke colors for a feature
    const getWeatherStyle = (feature) => {
        const match = findMatchingCircle(feature);
        if (!match || !weatherData || !weatherData[match.Code]) return null;
        const weather = weatherData[match.Code];
        const temp = weather.temp;
        const desc = (weather.description || '').toLowerCase();
        const isRain = desc.includes('rain') || desc.includes('storm') || desc.includes('thunder') || desc.includes('drizzle');

        let fillColor = '#81c784';
        let strokeColor = '#388e3c';
        if (temp >= 40) { fillColor = '#ef5350'; strokeColor = '#c62828'; }
        else if (isRain) { fillColor = '#42a5f5'; strokeColor = '#1565c0'; }
        else if (temp >= 38) { fillColor = '#ffa726'; strokeColor = '#e65100'; }
        return { fillColor, strokeColor };
    };

    const applyWeatherOverlays = (mapInstance) => {
        if (!mapInstance || !parentData || parentData.length === 0) {
            return;
        }

        // Create weather info overlays at polygon centroids
        const overlays = [];
        parentData.forEach((circle) => {
            // Use real weather data only
            const weather = weatherData?.[circle.Code];
            if (!weather) return; // Skip if no weather data available

            // Find the polygon bounds (min/max lat/lng)
            let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity, count = 0;
            const geoData = geoJSON || divisionGeoJson;
            if (geoData && geoData.features) {
                geoData.features.forEach(feature => {
                    const fName = (feature.properties?.Name || '').toLowerCase().trim();
                    const fCircle = (feature.properties?.Circle || '').toLowerCase().trim();
                    const fCircleCode = (feature.properties?.CircleCode || '').toString();
                    const cName = (circle.name || '').toLowerCase().trim();
                    const cCode = (circle.Code || '').toString();
                    const fNorm = fName.replace(/[\s.]+/g, '');
                    const cNorm = cName.replace(/[\s.]+/g, '');
                    const fCircleNorm = fCircle.replace(/[\s.]+/g, '');
                    const nameMatch = (fName && (fName === cName || fName.includes(cName) || cName.includes(fName) || fNorm === cNorm || fNorm.includes(cNorm) || cNorm.includes(fNorm)));
                    const circleMatch = (fCircle && (cName === fCircle || fCircle.includes(cName) || cName.includes(fCircle) || cNorm === fCircleNorm || fCircleNorm.includes(cNorm) || cNorm.includes(fCircleNorm)));
                    const codeMatch = (fCircleCode && cCode && fCircleCode === cCode.slice(0, 3));
                    if ((nameMatch || circleMatch || codeMatch) && feature.properties?.SHAPE === 'Polygon') {
                        if (feature.geometry && feature.geometry.coordinates) {
                            feature.geometry.coordinates.forEach(polygon => {
                                polygon.forEach(ring => {
                                    ring.forEach(coord => {
                                        if (coord[1] < minLat) minLat = coord[1];
                                        if (coord[1] > maxLat) maxLat = coord[1];
                                        if (coord[0] < minLng) minLng = coord[0];
                                        if (coord[0] > maxLng) maxLng = coord[0];
                                        count++;
                                    });
                                });
                            });
                        }
                    }
                });
            }

            // If no matching polygon found, try to get from any feature
            if (count === 0 && geoData && geoData.features) {
                for (const feature of geoData.features) {
                    if (feature.geometry && feature.geometry.coordinates && count < 100) {
                        const coords = feature.geometry.coordinates;
                        if (coords[0] && coords[0][0]) {
                            coords[0].forEach(ring => {
                                if (Array.isArray(ring) && ring.length === 2 && typeof ring[0] === 'number') {
                                    if (ring[1] < minLat) minLat = ring[1];
                                    if (ring[1] > maxLat) maxLat = ring[1];
                                    if (ring[0] < minLng) minLng = ring[0];
                                    if (ring[0] > maxLng) maxLng = ring[0];
                                    count++;
                                } else if (Array.isArray(ring)) {
                                    ring.forEach(coord => {
                                        if (Array.isArray(coord) && coord.length >= 2) {
                                            if (coord[1] < minLat) minLat = coord[1];
                                            if (coord[1] > maxLat) maxLat = coord[1];
                                            if (coord[0] < minLng) minLng = coord[0];
                                            if (coord[0] > maxLng) maxLng = coord[0];
                                            count++;
                                        }
                                    });
                                }
                            });
                        }
                    }
                    if (count >= 100) break;
                }
            }

            if (count === 0 || maxLat === -Infinity) {
                return;
            }

            // Position at 25% from bottom (lower quarter of polygon), centered horizontally
            const badgeLat = minLat + (maxLat - minLat) * 0.25;
            const badgeLng = minLng + (maxLng - minLng) * 0.5;

            const desc = (weather.description || '').toLowerCase();
            const isRain = desc.includes('rain') || desc.includes('storm') || desc.includes('thunder') || desc.includes('drizzle');
            const isHeatwave = weather.temp >= 40;

            // Determine colors based on weather condition
            let bgGradient = 'linear-gradient(135deg, #66BB6A, #43A047)'; // Mild - green
            let shadowColor = 'rgba(76, 175, 80, 0.4)';
            if (isHeatwave) {
                bgGradient = 'linear-gradient(135deg, #FF5252, #D32F2F)';
                shadowColor = 'rgba(244, 67, 54, 0.4)';
            } else if (isRain) {
                bgGradient = 'linear-gradient(135deg, #42A5F5, #1E88E5)';
                shadowColor = 'rgba(33, 150, 243, 0.4)';
            } else if (weather.temp >= 38) {
                bgGradient = 'linear-gradient(135deg, #FFB74D, #FB8C00)';
                shadowColor = 'rgba(255, 152, 0, 0.4)';
            }

            const content = `
                <div style="
                    background: ${bgGradient};
                    color: white;
                    padding: 4px 10px;
                    border-radius: 14px;
                    font-size: 12px;
                    font-weight: 700;
                    text-align: center;
                    white-space: nowrap;
                    pointer-events: none;
                    box-shadow: 0 3px 8px ${shadowColor}, 0 1px 3px rgba(0,0,0,0.2);
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
                    letter-spacing: 0.3px;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.2);
                ">
                    ${weather.temp}°C
                </div>
            `;

            const overlay = createCustomOverlay(new window.google.maps.LatLng(badgeLat, badgeLng), content, mapInstance);
            overlays.push(overlay);
        });

        weatherOverlaysRef.current = overlays;
        cachedWeatherOverlays = overlays;
    };

    useEffect(() => {
        displayModeRef.current = displayMode;

        // Always remove old heatmap layer and blink interval
        if (heatmapBlinkRef.current) {
            clearInterval(heatmapBlinkRef.current);
            heatmapBlinkRef.current = null;
        }
        if (heatmapOverlayRef.current) {
            clearComplaintHeatmapOverlay(heatmapOverlayRef.current);
            heatmapOverlayRef.current = null;
        }

        // Always remove old weather overlays
        if (weatherOverlaysRef.current && weatherOverlaysRef.current.length > 0) {
            weatherOverlaysRef.current.forEach(overlay => {
                try { overlay.setMap(null); } catch (e) { }
            });
            weatherOverlaysRef.current = [];
            cachedWeatherOverlays = [];
        }

        // Dull the background polygons in heatmap/weather mode, restore in other modes
        if (map && map.data) {
            if (displayMode === 'heatmap' || displayMode === 'weather') {
                map.data.forEach(feature => {
                    const feederCode = feature.getProperty('Feeder Code');
                    const isFeederLine =
                        feederCode && feature.getProperty('SHAPE') !== 'Polygon';
                    map.data.overrideStyle(feature, {
                        fillOpacity: 0.05,
                        strokeOpacity: isFeederLine ? 0.9 : 0.65,
                        strokeWeight: isFeederLine
                            ? Math.max(3.5, getFeederLineStrokeWeight(map) - 0.5)
                            : 1.25,
                    });
                });
            } else if (countDataFeatures(map) > 0) {
                applyCompanyPolygonStyle(map);
            }
        }

        if (isLoaded && map && hierarchy == 52) {
            getAPMSCoordinates(connections);
            if (displayMode === 'complaints') {
                getComplaintsCoordinates(filteredComplaints, true);
            } else if (displayMode === 'both') {
                // Complaints + line staff together (staff plotted in field-staff effect)
                getComplaintsCoordinates(filteredComplaints, false);
            } else if (displayMode === 'lineman') {
                // Line Staff only — clear complaint dots
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                    cachedComplaintMarkers = [];
                }
            } else if (displayMode === 'heatmap') {
                // Clear all existing markers for heatmap mode
                if (markers && markers.length > 0) {
                    markers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setMarkers([]);
                }
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                }
                // Create heatmap from complaints
                if (filteredComplaints && filteredComplaints.length > 0) {
                    mountComplaintHeatmap(map, filteredComplaints);
                }
            } else if (displayMode === 'weather') {
                // Clear all existing markers for weather mode
                if (markers && markers.length > 0) {
                    markers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setMarkers([]);
                }
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                }
                applyWeatherOverlays(map);
            }
        } else if (isLoaded && map) {
            // When returning to company level (homeButtonState is true), clear drill-down markers
            if (homeButtonState) {
                // Clear APMS markers
                clearHoverTooltip();
                clearFeederPolylines(map);
                clearMapBoundsRestriction(map);
                regionViewStateRef.current = null;
                feederLineFeaturesRef.current = [];
                apmsMarkers.forEach(marker => marker.setMap(null));
                setApmsMarkers([]);
                // Clear complaint markers
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                }
                // Do NOT clear Line Staff here when Line Staff / Both is active —
                // complaints refresh used to wipe markers and they never re-plotted
                // until the user toggled the tab.
                if (displayMode !== 'lineman' && displayMode !== 'both') {
                    const staffToClear =
                        (markers && markers.length > 0) ? markers : cachedFieldStaffMarkers;
                    if (staffToClear && staffToClear.length > 0) {
                        staffToClear.forEach(marker => {
                            try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                        });
                        setMarkers([]);
                        cachedFieldStaffMarkers = [];
                    }
                }
            }
            // All other levels (Company, Circle, Division): toggle
            // Show APMS when drilled down to subdivision level (but NOT when returning to company)
            if (setSubDivision && !homeButtonState) {
                getAPMSCoordinates(connections);
            } else {
                // Clear APMS markers when not at subdivision level OR when returning to company
                clearHoverTooltip();
                clearFeederPolylines(map);
                clearMapBoundsRestriction(map);
                regionViewStateRef.current = null;
                feederLineFeaturesRef.current = [];
                apmsMarkers.forEach(marker => marker.setMap(null));
                setApmsMarkers([]);
            }
            if (displayMode === 'complaints') {
                getComplaintsCoordinates(filteredComplaints, true);
            } else if (displayMode === 'both') {
                getComplaintsCoordinates(filteredComplaints, false);
                getFieldStaffCoordinates(filteredFieldStaff, false);
            } else if (displayMode === 'lineman') {
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                    cachedComplaintMarkers = [];
                }
                // Always (re)plot Line Staff — covers company open + complaints async load
                getFieldStaffCoordinates(filteredFieldStaff, false);
            } else if (displayMode === 'heatmap') {
                // Clear all existing markers
                if (markers && markers.length > 0) {
                    markers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setMarkers([]);
                }
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                }

                // Create heatmap from complaints (Pending + Allocated with valid coordinates)
                if (filteredComplaints && filteredComplaints.length > 0) {
                    mountComplaintHeatmap(map, filteredComplaints, true);
                }
            } else if (displayMode === 'weather') {
                // Clear all existing markers for weather mode
                if (markers && markers.length > 0) {
                    markers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setMarkers([]);
                }
                if (complaintMarkers && complaintMarkers.length > 0) {
                    complaintMarkers.forEach(marker => {
                        try { if (marker && marker.getMap()) marker.setMap(null); } catch (e) { }
                    });
                    setComplaintMarkers([]);
                }
                applyWeatherOverlays(map);
            }
        }
        if (selectedMarker) {
            onComplaintsUpdate(selectedMarker.complaitData);
        }
    }, [isLoaded, map, filteredComplaints, filteredFieldStaff, connections, circleCode, divisionCode, setSubDivision, hierarchy, displayMode, homeButtonState]);

    useEffect(() => {
        if (result1) {
            const feederInfor = result1.map(item => ({
                feeder_code: item.feeder_code,
                feeder: item.feeder,
                current_status: item.current_status,
                current: item.current
            }));
            setFeederInfo(feederInfor);
        }
    }, [result1]);

    const removeLeadingZeros = (feederArray) => {
        return feederArray.map(feeder => ({
            feeder_code: parseInt(feeder.feeder_code, 10),
            feeder: feeder.feeder,
            current_status: feeder.current_status,
            current: feeder.current,
            grid: feeder.grid
        }));
    };

    const getFeederPaletteColor = (feederCode) => {
        const palette = color ? FEEDER_LINE_PALETTE_LIGHT : FEEDER_LINE_PALETTE_DARK;
        return palette[hashFeederPaletteIndex(feederCode, palette.length)];
    };

    const isFeederNetworkView = () => hierarchy == 52 || !!setSubDivision;

    const mapRef = useRef(null);
    const lastMapLoadSignatureRef = useRef('');
    const lastPolygonLoadKeyRef = useRef(null);
    const prevHomeButtonRef = useRef(homeButtonState);

    const getGeoJsonDataKey = (geoJsonData, companyCode = '') => {
        const companyKey = companyCode?.toString() || 'none';
        if (!geoJsonData?.features?.length) return `${companyKey}:empty`;
        const hasFeeders = geoJsonData.features.some(
            (feature) => feature.properties?.['Feeder Code'] != null
        );
        return `${companyKey}:${geoJsonData.features.length}:${hasFeeders ? 'feeders' : 'regions'}`;
    };

    const countDataFeatures = (mapInstance) => {
        if (!mapInstance?.data) return 0;
        let count = 0;
        mapInstance.data.forEach(() => { count += 1; });
        return count;
    };

    const getPolygonLoadKey = (loadType) => {
        const officeKey =
            selectedCompanyCode?.toString() ||
            officeCode?.toString() ||
            code?.toString() ||
            '';
        return [
            loadType,
            hierarchy,
            homeButtonState,
            setCode || '',
            setDivision || '',
            setSubDivision || '',
            divisionCode?.toString()?.slice(0, 4) || '',
            officeKey.slice(0, 5),
            CircleCode?.toString()?.slice(0, 3) || '',
        ].join('|');
    };

    const shouldSkipPolygonReload = (mapInstance, loadKey) =>
        lastPolygonLoadKeyRef.current === loadKey && countDataFeatures(mapInstance) > 0;

    useEffect(() => {
        setData1(color ? [...FEEDER_LINE_PALETTE_LIGHT] : [...FEEDER_LINE_PALETTE_DARK]);
    }, [color]);

    useEffect(() => {
        if (mapRef.current && countDataFeatures(mapRef.current) > 0) {
            applyCompanyPolygonStyle(mapRef.current);
        }
    }, [data1, feederInfo, color]);

    useEffect(() => {
        const mapInstance = map || mapRef.current;
        if (!mapInstance || isFeederNetworkView()) return;
        applyCompanyLevelMapTheme(mapInstance);
        if (countDataFeatures(mapInstance) > 0) {
            applyCompanyPolygonStyle(mapInstance);
        }
    }, [color, map, hierarchy, setSubDivision, setCode, homeButtonState]);

    useEffect(() => {
        const mapInstance = map || mapRef.current;
        if (!mapInstance || !(hierarchy == 52 || setSubDivision)) return;
        if (!feederLineFeaturesRef.current.length) return;
        syncFeederPolylines(mapInstance, feederLineFeaturesRef.current, {
            feederStatusList: feederInfo?.length ? feederInfo : result1,
            lightMode: color,
        });
        if (countDataFeatures(mapInstance) > 0) {
            applyCompanyPolygonStyle(mapInstance);
        }
    }, [map, feederInfo, result1, color, hierarchy, setSubDivision]);

    const colorByFeederCode = (feederCode, feederInfo) => {
        if (!feederInfo?.length || !data1?.length) return null;
        const feederCodeData = removeLeadingZeros(feederInfo);
        const feeder = feederCodeData.find(f => f.feeder_code == feederCode);
        if (!feeder) return null;
        if (feeder.current <= 3 && feeder.current_status == "OFF") {
            return color ? '#EF5350' : '#C62828';
        }
        return data1[hashFeederPaletteIndex(feederCode, data1.length)];
    };

    const flashInterval = 1000;

    useEffect(() => {
        const intervalId = setInterval(() => {
            setFlash((prevFlash) => !prevFlash);
        }, flashInterval);

        return () => clearInterval(intervalId);
    }, [flashInterval]);

    const resetStyling = (map) => {
        map.setOptions({ styles: '' });
    };

    const mapLoadSubdivision = async (map) => {
        if (map) {
            mapRef.current = map;
            clearFeederPolylines(map);
            clearMapBoundsRestriction(map);
            regionViewStateRef.current = null;
            feederLineFeaturesRef.current = [];
            map.data.forEach((feature) => {
                map.data.remove(feature);
            });
            map.setOptions({
                styles: [],
            });

            if (geoJSON) {
                const { polygons, lines } = splitGeoJsonForFeederView(geoJSON);
                feederLineFeaturesRef.current = lines;
                map.data.addGeoJson({
                    type: 'FeatureCollection',
                    features: polygons,
                });
                applyCompanyPolygonStyle(map);
                syncFeederPolylines(map, lines, {
                    feederStatusList: result1 || feederInfo,
                    lightMode: color,
                });
                if ((hierarchy == 52 || setSubDivision) && lines.length > 0) {
                    fitMapToFeederNetwork(map, lines, {
                        connections,
                        padding: 32,
                        restrictPan: true,
                    });
                }
            }
            setMap(map);

            // Load APMS markers after map is ready for hierarchy 52 or when drilling down to subdivision
            if ((hierarchy == 52 || setSubDivision) && connections && connections.length > 0) {
                // Clear existing APMS markers
                clearHoverTooltip();
                apmsMarkers.forEach(marker => marker.setMap(null));

                const newApmsMarkers = [];
                connections.forEach(connection => {
                    if (connection.latitude && connection.longitude) {
                        const marker = new window.google.maps.Marker({
                            position: { lat: parseFloat(connection.latitude), lng: parseFloat(connection.longitude) },
                            icon: {
                                url: (connection.contactor_status === 0) ? triangleoff : triangleon,
                                size: new window.google.maps.Size(28, 28),
                                scaledSize: new window.google.maps.Size(28, 28)
                            },
                            map,
                            title: connection.id,
                            zIndex: 100
                        });

                        const contentString = `
                        <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; z-index=2000 ">
                            <div >
                                <span style="flex-grow: 1; font-size: 16px; font-weight: bold; color: #fdff02;">
                                    ${connection.name || 'N/A'}
                                </span>
                            </div>
                            <br />
                            <p style="margin: 5px 0;"><strong>MSN:</strong> ${connection.msn || 'N/A'}</p>
                            <p style="margin: 5px 0; color: #fdff02;"><strong>Load:</strong> ${connection.kva} kVA</p>
                            <p style="margin: 5px 0;"><strong>Last Comm:</strong> ${connection.last_communication_time || 'N/A'}</p>
                            <p style="margin: 5px 0;"><strong>Contactor status:</strong> <span style="color: ${connection.contactor_status === 0 ? '#e74c3c' : '#2ecc71'}; font-weight: bold;">${getApmsContactorStatusLabel(connection.contactor_status)}</span></p>
                        </div>`;

                        attachMarkerHoverTooltip(marker, contentString, map);
                        marker.addListener('click', () => {
                            clearHoverTooltip();
                            setSelectedMarker(connection);
                        });

                        newApmsMarkers.push(marker);
                    }
                });
                setApmsMarkers(newApmsMarkers);

                if (feederLineFeaturesRef.current.length > 0) {
                    fitMapToFeederNetwork(map, feederLineFeaturesRef.current, {
                        connections,
                        padding: 32,
                        restrictPan: true,
                    });
                }
            }
        }
    };

    const getStableFeatureColor = (feature) => getRegionPolygonFillColor(feature, color);

    const applyCompanyPolygonStyle = (mapInstance) => {
        if (!mapInstance?.data) return;

        clearFeederLineHoverHighlight(mapInstance);
        mapInstance.data.revertStyle();

        const feederView = isFeederNetworkView();

        mapInstance.data.setStyle((feature) => {
            const shapeType = feature.getProperty('SHAPE');
            const feederCode = feature.getProperty('Feeder Code');
            const isPolygon = shapeType === 'Polygon';
            const isFeederLine = feederCode && !isPolygon;
            const isHeatmap = displayModeRef.current === 'heatmap' || displayModeRef.current === 'weather';

            if (isFeederLine) {
                return {
                    strokeOpacity: 0,
                    fillOpacity: 0,
                    clickable: false,
                    visible: false,
                };
            }

            if (displayModeRef.current === 'weather' && isPolygon) {
                const wStyle = getWeatherStyle(feature);
                if (wStyle) {
                    return {
                        strokeColor: wStyle.strokeColor,
                        strokeWeight: 2,
                        strokeOpacity: 1,
                        fillColor: wStyle.fillColor,
                        fillOpacity: 0.45,
                    };
                }
            }

            const regionLightMode = color;
            const regionFill = isPolygon ? getRegionPolygonFillColor(feature, regionLightMode) : '#FFFFFF';

            return {
                strokeColor: feederView
                    ? '#B0BEC5'
                    : (regionLightMode ? '#78909C' : '#546E7A'),
                strokeWeight: feederView ? 0.75 : (isPolygon ? 1.25 : 1.25),
                strokeOpacity: feederView
                    ? 0.35
                    : (isHeatmap ? 0.65 : (isPolygon ? 0.65 : 0.75)),
                fillColor: regionFill,
                fillOpacity: feederView
                    ? 0.05
                    : (isHeatmap ? 0.05 : (isPolygon ? (regionLightMode ? 0.3 : 0.36) : 0)),
                clickable: feederView ? false : true,
                zIndex: feederView ? 1 : undefined,
            };
        });
    };

    const applyCompanyLevelMapTheme = (mapInstance) => {
        if (!mapInstance || isFeederNetworkView()) return;
        mapInstance.setOptions({
            styles: color ? mapStyleCompanyLight : (setCode ? mapStyle1 : mapStyle),
        });
    };

    const [divisionName, setDivisionName] = useState(null);
    const [circleName, setCircleName] = useState(null);

    const getMapDrillLevel = () => {
        if (setSubDivision) return 'feeder';
        if (setDivision) return 'subdivision';
        if (setCode || circleName) return 'division';
        return 'circle';
    };

    const handleRegionMapClick = (event) => {
        const feature = event?.feature;
        if (!feature?.getProperty) return;

        const name = feature.getProperty('Name');
        const circleProp = feature.getProperty('Circle');
        const circleCodeProp = feature.getProperty('CircleCode');
        const rawOfficeCode = feature.getProperty('OfficeCode');
        const subdivisionCodeProp =
            feature.getProperty('SubDivisionCode') ||
            feature.getProperty('SubdivisionCode') ||
            feature.getProperty('subdivisionCode') ||
            (rawOfficeCode && String(rawOfficeCode).length >= 5 ? rawOfficeCode : null);
        const divisionCodeProp =
            feature.getProperty('DivisionCode') ||
            feature.getProperty('divisionCode') ||
            (rawOfficeCode && String(rawOfficeCode).length === 4 ? rawOfficeCode : null);
        const level = getMapDrillLevel();

        if (level === 'feeder') return;

        // Parent handles selection when callback is provided — avoid local state that re-triggers map reload
        if (!onRegionClick) {
            if (level === 'subdivision') {
                setDivisionName(name);
            } else if (level === 'division') {
                setDivisionName(name);
            } else {
                setCircleName(name);
                setDivisionName("");
            }
        }

        onRegionClick?.({
            name,
            circle: circleProp,
            circleCode: circleCodeProp,
            divisionCode: divisionCodeProp,
            subdivisionCode: subdivisionCodeProp,
            level,
        });
    };

    useEffect(() => {
        if (!setCode) {
            setCircleName("");
            setDivisionName("");
        }
    }, [setCode]);

    const mapStyle = [
        {
            "featureType": "all",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }, { "lightness": -25 }, { "saturation": -50 }]
        },
        {
            "featureType": "road.highway",
            "elementType": "labels",
            "stylers": [{ "color": "#d3d3d3" }]
        },
        {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }]
        },
        {
            "featureType": "road",
            "elementType": "labels",
            "stylers": [{ "visibility": "on" }]
        },
        {
            "featureType": "landscape",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }]
        },
        {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }, { "visibility": "off" }]
        },
        {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [{ "color": "#a0d6d1" }]
        },
    ];

    const mapStyle1 = [
        {
            "featureType": "all",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }, { "lightness": -25 }, { "saturation": -50 }]
        },
        {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }]
        },
        {
            "featureType": "road",
            "elementType": "labels",
            "stylers": [{ "visibility": "on" }]
        },
        {
            "featureType": "landscape",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }]
        },
        {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [{ "color": "#d3d3d3" }, { "visibility": "off" }]
        },
        {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [{ "color": "#a0d6d1" }]
        },
    ];

    const mapLoadCompany = async (map) => {
        if (map) {
            mapRef.current = map;
            clearMapBoundsRestriction(map);
            regionViewStateRef.current = null;
            applyCompanyLevelMapTheme(map);
            if (geoJSON || divisionGeoJson) {
                const latitudes = [];
                const longitudes = [];

                let filteredGeoJSON = {};


                if (homeButtonState) {
                    const regionPolygons = (geoJSON?.features || []).filter(
                        (item) =>
                            item.properties?.['SHAPE'] === 'Polygon' &&
                            item.properties?.['Feeder Code'] == null
                    );
                    if (regionPolygons.length) {
                        filteredGeoJSON = {
                            type: 'FeatureCollection',
                            features: regionPolygons,
                        };
                    } else if (divisionGeoJson?.features?.length) {
                        filteredGeoJSON = {
                            type: 'FeatureCollection',
                            features: (divisionGeoJson.features || []).filter(
                                (item) => item.properties?.['SHAPE'] === 'Polygon'
                            ),
                        };
                    }
                } else if ((setCode && !setDivision && code == "11" && homeButtonState == false)) {
                    filteredGeoJSON = {
                        type: "FeatureCollection",
                        features: geoJSON.features.filter(
                            (item) => item.properties['SHAPE'] === "Polygon" && item.properties.Circle == setCode
                        ),
                    };
                } else if ((setCode && !setDivision && homeButtonState == false)) {
                    filteredGeoJSON = {
                        type: "FeatureCollection",
                        features: (divisionGeoJson?.features || []).filter(
                            (item) => item.properties['SHAPE'] === "Polygon" && item.properties.Circle == setCode
                        ),
                    };
                } else if (setDivision && !setSubDivision && homeButtonState == false) {
                    const divCodeToUse = divisionCode?.toString()?.slice(0, 4);
                    filteredGeoJSON = {
                        type: "FeatureCollection",
                        features: (divisionGeoJson?.features || []).filter((item) => {
                            if (item.properties['SHAPE'] !== "Polygon") return false;
                            const featureDivCode = (
                                item.properties.DivisionCode ||
                                item.properties.divisionCode ||
                                ''
                            ).toString();
                            if (divCodeToUse && featureDivCode === divCodeToUse) {
                                return true;
                            }
                            return (
                                mapRegionNamesMatch(item.properties.Circle, setCode) &&
                                divisionGeoJsonNameMatches(item.properties.Name, setDivision)
                            );
                        }),
                    };
                } else {
                    if (geoJSON && geoJSON.features) {
                        filteredGeoJSON = {
                            type: "FeatureCollection",
                            features: geoJSON.features.filter(
                                (item) => item.properties['SHAPE'] === "Polygon"
                            ),
                        };
                    } else {
                        let codeToGeoJSON = code;
                        await dispatch(fetchGeoJson({ subDivisionCode: codeToGeoJSON.slice(0, 2), roleTypeId: roleTypeId }));
                    }
                }

                if (!filteredGeoJSON?.features?.length) {
                    clearDataLayerListeners(map);
                    map.data.forEach((feature) => {
                        map.data.remove(feature);
                    });
                    lastPolygonLoadKeyRef.current = null;
                    setMap(map);
                    return;
                }

                const loadKey = getPolygonLoadKey('company');
                if (shouldSkipPolygonReload(map, loadKey)) {
                    applyRegionViewLimits(map, regionViewStateRef.current);
                    ensureRegionClickListener(map, handleRegionMapClick);
                    setMap(map);
                    return;
                }
                lastPolygonLoadKeyRef.current = loadKey;

                filteredGeoJSON.features.forEach((item) => {
                    item.geometry.coordinates.forEach((polygon) => {
                        polygon.forEach((ring) => {
                            ring.forEach((coord) => {
                                longitudes.push(coord[0]);
                                latitudes.push(coord[1]);
                            });
                        });
                    });
                });

                const minLat = Math.min(...latitudes);
                const maxLat = Math.max(...latitudes);
                const minLng = Math.min(...longitudes);
                const maxLng = Math.max(...longitudes);
                const extents = { minLat, maxLat, minLng, maxLng };
                regionViewStateRef.current = { extents, minZoom: null };

                if (window.google) {
                    fitMapToRegionView(map, extents, {
                        onLimitsApplied: ({ minZoom }) => {
                            regionViewStateRef.current = { extents, minZoom };
                        },
                    });
                }

                clearDataLayerListeners(map);
                map.data.forEach((feature) => {
                    map.data.remove(feature);
                });
                map.data.addGeoJson(filteredGeoJSON);
                applyCompanyPolygonStyle(map);
                map.data.addListener('mouseover', (event) => {
                    const name = event.feature.getProperty('Name');
                    const circleProp = event.feature.getProperty('Circle');
                    const circleCode = event.feature.getProperty('CircleCode');

                    // Get weather info if in heatmap mode
                    let weatherHtml = '';
                    if (displayModeRef.current === 'heatmap' && weatherData && parentData) {

                        // Helper function to normalize names for comparison
                        const normalize = (str) => (str || '').toLowerCase().trim().replace(/[\s.]+/g, '');

                        // Try to find matching circle or division
                        let matchedCircle = null;
                        let matchedWeatherCode = null;

                        const fName = (name || '').toLowerCase().trim();
                        const fCircle = (circleProp || '').toLowerCase().trim();
                        const fNorm = normalize(name);
                        const fCircleNorm = normalize(circleProp);

                        // First try to match by circle code
                        if (circleCode) {
                            matchedCircle = parentData.find(p => {
                                const pCode = (p.Code || '').toString();
                                return pCode.startsWith(circleCode.toString().slice(0, 3));
                            });
                        }

                        // If not found, try matching by name against circles
                        if (!matchedCircle) {
                            matchedCircle = parentData.find(p => {
                                const pName = (p.name || '').toLowerCase().trim();
                                const pNorm = normalize(p.name);

                                // Direct match or contains match
                                if (fCircle && (pName === fCircle || fCircle.includes(pName) || pName.includes(fCircle) || pNorm === fCircleNorm || fCircleNorm.includes(pNorm) || pNorm.includes(fCircleNorm))) return true;
                                if (fName && (pName === fName || fName.includes(pName) || pName.includes(fName) || pNorm === fNorm || fNorm.includes(pNorm) || pNorm.includes(fNorm))) return true;
                                return false;
                            });
                        }

                        // If still not found, check if the name matches a division (child of a circle)
                        if (!matchedCircle) {
                            for (const parent of parentData) {
                                if (parent.children && parent.children.length > 0) {
                                    const matchedChild = parent.children.find(child => {
                                        const cName = (child.name || '').toLowerCase().trim();
                                        const cNorm = normalize(child.name);
                                        return fName === cName || fNorm === cNorm || fName.includes(cName) || cName.includes(fName) || fNorm.includes(cNorm) || cNorm.includes(fNorm);
                                    });
                                    if (matchedChild) {
                                        matchedWeatherCode = matchedChild.Code;
                                        if (!weatherData[matchedChild.Code]) {
                                            matchedCircle = parent;
                                            matchedWeatherCode = parent.Code;
                                        }
                                        break;
                                    }
                                }
                            }
                        }

                        if (matchedCircle && !matchedWeatherCode) {
                            matchedWeatherCode = matchedCircle.Code;
                        }

                        if (matchedCircle && matchedWeatherCode) {
                            const weather = weatherData[matchedWeatherCode];
                            if (weather) {
                                const desc = (weather.description || '').toLowerCase();
                                const isRain = desc.includes('rain') || desc.includes('storm') || desc.includes('thunder');
                                const isHeatwave = weather.temp >= 40;
                                let bgColor = '#4CAF50';
                                if (isHeatwave) bgColor = '#F44336';
                                else if (isRain) bgColor = '#2196F3';
                                else if (weather.temp >= 38) bgColor = '#FF9800';
                                weatherHtml = `
                                    <div style="margin-top: 8px; padding: 6px 10px; background: ${bgColor}; border-radius: 8px; display: inline-block;">
                                        <span style="font-size: 18px; font-weight: bold;">${weather.temp}°C</span>
                                    </div>
                                `;
                            }
                        }
                    }

                    const contentString = `
                            <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; min-width: 150px;">
                              <div style="display: flex;">
                                <span style="flex-grow: 1; font-size: 16px; font-weight: bold;">
                                  ${name}
                                </span>
                              </div>
                              ${weatherHtml}
                            </div>
                        `;
                    showHoverTooltip(event.latLng, contentString, map);
                });

                attachMapRegionClickHandler(map, handleRegionMapClick);
                map.data.addListener('mouseout', clearHoverTooltip);
            }
            setMap(map);
        }
    };

    const mapLoadCircle = async (map) => {
        if (map) {
            mapRef.current = map;
            applyCompanyLevelMapTheme(map);
            if (geoJSON) {
                const latitudes = [];
                const longitudes = [];

                let filteredGeoJSON = {};


                if (setDivision) {
                    filteredGeoJSON = {
                        type: "FeatureCollection",
                        features: (divisionGeoJson?.features || []).filter(
                            (item) =>
                                item.properties['SHAPE'] === "Polygon" &&
                                item.properties.CircleCode == (hierarchy == 3 ? (CircleCode || officeCode) : officeCode) &&
                                divisionGeoJsonNameMatches(item.properties.Name, setDivision)
                        ),
                    };
                } else {
                    const circleCodeToUse = hierarchy == 3 ? (CircleCode || code) : (officeCode ? officeCode : code);
                    filteredGeoJSON = {
                        type: "FeatureCollection",
                        features: (divisionGeoJson?.features || []).filter(
                            (item) => item.properties['SHAPE'] === "Polygon" && item.properties.CircleCode == circleCodeToUse
                        ),
                    };
                }

                if (!filteredGeoJSON?.features?.length) {
                    clearDataLayerListeners(map);
                    map.data.forEach((feature) => {
                        map.data.remove(feature);
                    });
                    lastPolygonLoadKeyRef.current = null;
                    setMap(map);
                    return;
                }

                const circleLoadKey = getPolygonLoadKey('circle');
                if (shouldSkipPolygonReload(map, circleLoadKey)) {
                    applyRegionViewLimits(map, regionViewStateRef.current);
                    ensureRegionClickListener(map, handleRegionMapClick);
                    setMap(map);
                    return;
                }
                lastPolygonLoadKeyRef.current = circleLoadKey;

                filteredGeoJSON.features.forEach((item) => {
                    item.geometry.coordinates.forEach((polygon) => {
                        polygon.forEach((ring) => {
                            ring.forEach((coord) => {
                                longitudes.push(coord[0]);
                                latitudes.push(coord[1]);
                            });
                        });
                    });
                });
                const minLat = Math.min(...latitudes);
                const maxLat = Math.max(...latitudes);
                const minLng = Math.min(...longitudes);
                const maxLng = Math.max(...longitudes);
                const extents = { minLat, maxLat, minLng, maxLng };
                regionViewStateRef.current = { extents, minZoom: null };

                if (window.google) {
                    fitMapToRegionView(map, extents, {
                        onLimitsApplied: ({ minZoom }) => {
                            regionViewStateRef.current = { extents, minZoom };
                        },
                    });
                }

                clearDataLayerListeners(map);
                map.data.forEach((feature) => {
                    map.data.remove(feature);
                });
                map.data.addGeoJson(filteredGeoJSON);
                applyCompanyPolygonStyle(map);
                map.data.addListener('mouseover', (event) => {
                    const name = event.feature.getProperty('Name');
                    const circleProp = event.feature.getProperty('Circle');
                    const circleCode = event.feature.getProperty('CircleCode');

                    // Get weather info if in heatmap mode
                    let weatherHtml = '';
                    if (displayModeRef.current === 'heatmap' && weatherData && parentData) {

                        // Helper function to normalize names for comparison
                        const normalize = (str) => (str || '').toLowerCase().trim().replace(/[\s.]+/g, '');

                        // Try to find matching circle or division
                        let matchedCircle = null;
                        let matchedWeatherCode = null;

                        const fName = (name || '').toLowerCase().trim();
                        const fCircle = (circleProp || '').toLowerCase().trim();
                        const fNorm = normalize(name);
                        const fCircleNorm = normalize(circleProp);

                        // First try to match by circle code
                        if (circleCode) {
                            matchedCircle = parentData.find(p => {
                                const pCode = (p.Code || '').toString();
                                return pCode.startsWith(circleCode.toString().slice(0, 3));
                            });
                        }

                        // If not found, try matching by name against circles
                        if (!matchedCircle) {
                            matchedCircle = parentData.find(p => {
                                const pName = (p.name || '').toLowerCase().trim();
                                const pNorm = normalize(p.name);

                                // Direct match or contains match
                                if (fCircle && (pName === fCircle || fCircle.includes(pName) || pName.includes(fCircle) || pNorm === fCircleNorm || fCircleNorm.includes(pNorm) || pNorm.includes(fCircleNorm))) return true;
                                if (fName && (pName === fName || fName.includes(pName) || pName.includes(fName) || pNorm === fNorm || fNorm.includes(pNorm) || pNorm.includes(fNorm))) return true;
                                return false;
                            });
                        }

                        // If still not found, check if the name matches a division (child of a circle)
                        if (!matchedCircle) {
                            for (const parent of parentData) {
                                if (parent.children && parent.children.length > 0) {
                                    const matchedChild = parent.children.find(child => {
                                        const cName = (child.name || '').toLowerCase().trim();
                                        const cNorm = normalize(child.name);
                                        return fName === cName || fNorm === cNorm || fName.includes(cName) || cName.includes(fName) || fNorm.includes(cNorm) || cNorm.includes(fNorm);
                                    });
                                    if (matchedChild) {
                                        matchedWeatherCode = matchedChild.Code;
                                        if (!weatherData[matchedChild.Code]) {
                                            matchedCircle = parent;
                                            matchedWeatherCode = parent.Code;
                                        }
                                        break;
                                    }
                                }
                            }
                        }

                        if (matchedCircle && !matchedWeatherCode) {
                            matchedWeatherCode = matchedCircle.Code;
                        }

                        if (matchedCircle && matchedWeatherCode) {
                            const weather = weatherData[matchedWeatherCode];
                            if (weather) {
                                const desc = (weather.description || '').toLowerCase();
                                const isRain = desc.includes('rain') || desc.includes('storm') || desc.includes('thunder');
                                const isHeatwave = weather.temp >= 40;
                                let bgColor = '#4CAF50';
                                if (isHeatwave) bgColor = '#F44336';
                                else if (isRain) bgColor = '#2196F3';
                                else if (weather.temp >= 38) bgColor = '#FF9800';
                                weatherHtml = `
                                    <div style="margin-top: 8px; padding: 6px 10px; background: ${bgColor}; border-radius: 8px; display: inline-block;">
                                        <span style="font-size: 18px; font-weight: bold;">${weather.temp}°C</span>
                                    </div>
                                `;
                            }
                        }
                    }

                    const contentString = `
                            <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; min-width: 150px;">
                              <div style="display: flex;">
                                <span style="flex-grow: 1; font-size: 16px; font-weight: bold;">
                                  ${name}
                                </span>
                              </div>
                              ${weatherHtml}
                            </div>
                        `;
                    showHoverTooltip(event.latLng, contentString, map);
                });

                attachMapRegionClickHandler(map, handleRegionMapClick);
                map.data.addListener('mouseout', clearHoverTooltip);
            }
            setMap(map);
        }
    };

    // Load subdivision view for hierarchy 4 when geoJSON is not available but divisionGeoJson is
    const mapLoadSubdivisionFromDivision = async (map) => {
        if (map && divisionGeoJson && setSubDivision) {
            mapRef.current = map;
            map.data.forEach((feature) => {
                map.data.remove(feature);
            });
            map.setOptions({
                styles: [],
            });

            const latitudes = [];
            const longitudes = [];

            // Get subdivision code - take first 5 digits
            const subDivCode = setSubDivision?.toString()?.slice(0, 5);
            const divCode = setSubDivision?.toString()?.slice(0, 4);

            // Filter divisionGeoJson to get the selected subdivision's or division's boundary
            const filteredGeoJSON = {
                type: "FeatureCollection",
                features: (divisionGeoJson?.features || []).filter((item) => {
                    if (item.properties['SHAPE'] !== "Polygon") return false;
                    // Try multiple property names for matching
                    const props = item.properties;
                    const featureSubDivCode = (props.SubDivisionCode || props.subdivisionCode || props.SubdivisionCode || '').toString();
                    const featureDivCode = (props.DivisionCode || props.divisionCode || '').toString();
                    const featureName = (props.Name || props.name || '').toString();

                    // Match by subdivision code, division code, or by checking if within the same division
                    return featureSubDivCode === subDivCode ||
                           featureDivCode === divCode ||
                           featureDivCode === subDivCode?.slice(0, 4);
                }),
            };

            // If no features found, fallback to showing the division area
            if (filteredGeoJSON.features.length === 0) {
                // Try to find any polygon in the current division
                filteredGeoJSON.features = (divisionGeoJson?.features || []).filter((item) => {
                    if (item.properties['SHAPE'] !== "Polygon") return false;
                    const props = item.properties;
                    const featureDivCode = (props.DivisionCode || props.divisionCode || '').toString();
                    const featureCircleCode = (props.CircleCode || props.circleCode || '').toString();
                    // Match by division or circle
                    return featureDivCode === divCode || featureCircleCode === subDivCode?.slice(0, 3);
                });
            }

            if (filteredGeoJSON.features.length > 0) {
                filteredGeoJSON.features.forEach((item) => {
                    if (item.geometry?.coordinates) {
                        item.geometry.coordinates.forEach((polygon) => {
                            polygon.forEach((ring) => {
                                ring.forEach((coord) => {
                                    longitudes.push(coord[0]);
                                    latitudes.push(coord[1]);
                                });
                            });
                        });
                    }
                });

                if (latitudes.length > 0 && longitudes.length > 0) {
                    const minLat = Math.min(...latitudes);
                    const maxLat = Math.max(...latitudes);
                    const minLng = Math.min(...longitudes);
                    const maxLng = Math.max(...longitudes);

                    if (window.google) {
                        const bounds = new window.google.maps.LatLngBounds();
                        bounds.extend(new window.google.maps.LatLng(minLat, minLng));
                        bounds.extend(new window.google.maps.LatLng(maxLat, maxLng));
                        map.fitBounds(bounds);

                        const listener = window.google.maps.event.addListener(map, "bounds_changed", () => {
                            const currentZoom = map.getZoom();
                            if (currentZoom < 12) {
                                map.setZoom(12);
                            }
                            window.google.maps.event.removeListener(listener);
                        });
                    }
                }

                map.data.addGeoJson(filteredGeoJSON);
                map.data.setStyle((feature) => ({
                    strokeColor: '#4285F4',
                    strokeWeight: 2,
                    fillColor: '#4285F4',
                    fillOpacity: 0.1,
                }));
            }
            setMap(map);
        }
    };

    const mapLoadDivision = async (map) => {

        if (map) {
            mapRef.current = map;
            applyCompanyLevelMapTheme(map);

            const latitudes = [];
            const longitudes = [];

            let filteredGeoJSON = {};

            if (setDivision) {
                filteredGeoJSON = {
                    type: "FeatureCollection",
                    features: (divisionGeoJson?.features || []).filter(
                        (item) =>
                            item.properties['SHAPE'] === "Polygon" &&
                            mapRegionNamesMatch(item.properties.Circle, setCode || "MULTAN") &&
                            divisionGeoJsonNameMatches(item.properties.Name, setDivision)
                    ),
                };
            } else {
                // Use officeCode if available, otherwise use code from auth
                const officeCodeStr = officeCode?.toString() || code?.toString() || '';
                filteredGeoJSON = {
                    type: "FeatureCollection",
                    features: (divisionGeoJson?.features || []).filter(
                        (item) => {
                            if (item.properties['SHAPE'] !== "Polygon") return false;
                            // For hierarchy 4 (division level), filter by DivisionCode only
                            if (hierarchy == 4) {
                                return item.properties.DivisionCode == officeCodeStr.slice(0, 4);
                            }
                            // For other levels, filter by both CircleCode and DivisionCode
                            return item.properties.CircleCode == officeCodeStr.slice(0, 3) && item.properties.DivisionCode == officeCodeStr.slice(0, 4);
                        }
                    ),
                };
            }

            if (!filteredGeoJSON?.features?.length) {
                clearDataLayerListeners(map);
                map.data.forEach((feature) => {
                    map.data.remove(feature);
                });
                lastPolygonLoadKeyRef.current = null;
                setMap(map);
                return;
            }

            const divisionLoadKey = getPolygonLoadKey('division');
            if (shouldSkipPolygonReload(map, divisionLoadKey)) {
                applyRegionViewLimits(map, regionViewStateRef.current);
                ensureRegionClickListener(map, handleRegionMapClick);
                setMap(map);
                return;
            }
            lastPolygonLoadKeyRef.current = divisionLoadKey;

            filteredGeoJSON.features.forEach((item) => {
                item.geometry.coordinates.forEach((polygon) => {
                    polygon.forEach((ring) => {
                        ring.forEach((coord) => {
                            longitudes.push(coord[0]);
                            latitudes.push(coord[1]);
                        });
                    });
                });
            });
            const minLat = Math.min(...latitudes);
            const maxLat = Math.max(...latitudes);
            const minLng = Math.min(...longitudes);
            const maxLng = Math.max(...longitudes);
            const extents = { minLat, maxLat, minLng, maxLng };
            regionViewStateRef.current = { extents, minZoom: null };

            if (window.google) {
                fitMapToRegionView(map, extents, {
                    onLimitsApplied: ({ minZoom }) => {
                        regionViewStateRef.current = { extents, minZoom };
                    },
                });
            }

            clearDataLayerListeners(map);
            map.data.forEach((feature) => {
                map.data.remove(feature);
            });
            map.data.addGeoJson(filteredGeoJSON);
            applyCompanyPolygonStyle(map);
            map.data.addListener('mouseover', (event) => {
                const name = event.feature.getProperty('Name');

                const contentString = `
                            <div style="padding: 10px; background-color: #333; border-radius: 10px; color: white; font-family: Arial, sans-serif; width: 200px;">
                              <div style="display: flex;">
                                <span style="flex-grow: 1; font-size: 16px; font-weight: bold;">
                                  ${name}
                                </span>
                              </div>
                            </div>
                        `;
                showHoverTooltip(event.latLng, contentString, map);
            });

            attachMapRegionClickHandler(map, handleRegionMapClick);
            map.data.addListener('mouseout', clearHoverTooltip);
            setMap(map);
        }
    };

    // Re-attach region click whenever drill state or polygon data changes
    useEffect(() => {
        const mapInstance = map || mapRef.current;
        if (!mapInstance || !onRegionClick) return;
        if (countDataFeatures(mapInstance) === 0) return;
        attachMapRegionClickHandler(mapInstance, handleRegionMapClick);
    }, [map, onRegionClick, setCode, setDivision, setSubDivision, homeButtonState, geoJSON, divisionGeoJson]);

    // Dismiss hover tooltip when pointer leaves map or map moves
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;

        const mapListeners = [
            map.addListener('click', () => clearMapHoverUi(map)),
            map.addListener('dragstart', () => clearMapHoverUi(map)),
            map.addListener('zoom_changed', () => {
                clearMapHoverUi(map);
                updateFeederPolylineStrokeWeights(map);
                if (countDataFeatures(map) > 0) {
                    applyCompanyPolygonStyle(map);
                }
            }),
        ];

        const mapDiv = map.getDiv?.();
        const handleMapMouseLeave = () => clearMapHoverUi(map);
        mapDiv?.addEventListener('mouseleave', handleMapMouseLeave);

        return () => {
            mapListeners.forEach((listener) => {
                window.google.maps.event.removeListener(listener);
            });
            mapDiv?.removeEventListener('mouseleave', handleMapMouseLeave);
            clearMapHoverUi(map);
        };
    }, [map]);

    // Build lineman markers when map becomes ready or staff data changes
    useEffect(() => {
        if (!isLoaded || !map) return;

        const clearLinemanMarkers = () => {
            if (markers && markers.length > 0) {
                markers.forEach((marker) => {
                    try {
                        if (marker && marker.getMap()) marker.setMap(null);
                    } catch (e) { /* marker already removed */ }
                });
                setMarkers([]);
                cachedFieldStaffMarkers = [];
            }
        };

        if (displayMode === 'heatmap' || displayMode === 'weather' || displayMode === 'complaints') {
            clearLinemanMarkers();
            return;
        }

        if (displayMode === 'lineman' || displayMode === 'both') {
            // Never clear complaint dots from the field-staff update — complaint
            // markers are managed by the complaints displayMode effect.
            getFieldStaffCoordinates(filteredFieldStaff, false);
        }
    }, [isLoaded, map, fieldStaffMarkerKey, displayMode, homeButtonState]);

    useEffect(() => {
        if (homeButtonState && !prevHomeButtonRef.current) {
            lastMapLoadSignatureRef.current = '';
            lastPolygonLoadKeyRef.current = null;
        }
        prevHomeButtonRef.current = homeButtonState;
    }, [homeButtonState]);

    useEffect(() => {
        lastMapLoadSignatureRef.current = '';
        lastPolygonLoadKeyRef.current = null;
        // Company open/switch: always show that company's Line Staff by default
        setDisplayMode('lineman');
        setSelectedDesignationFilter('all');
    }, [selectedCompanyCode]);

    useEffect(() => {
        // Check if geoJSON has actual features
        const hasGeoJSONFeatures = geoJSON && geoJSON.features && geoJSON.features.length > 0;
        const hasDivisionGeoJson = divisionGeoJson && divisionGeoJson.features && divisionGeoJson.features.length > 0;
        const geoJsonDataKey = getGeoJsonDataKey(geoJSON, selectedCompanyCode);

        const loadSignature = [
            hierarchy,
            selectedCompanyCode || '',
            setSubDivision || '',
            setCode || '',
            setDivision || '',
            circleCode?.toString()?.slice(0, 3) || '',
            divisionCode?.toString()?.slice(0, 4) || '',
            homeButtonState,
            hasGeoJSONFeatures,
            hasDivisionGeoJson,
            geoJsonDataKey,
        ].join('|');

        if (!map || loadSignature === lastMapLoadSignatureRef.current) {
            return;
        }
        lastMapLoadSignatureRef.current = loadSignature;


        // PRIORITY: When homeButtonState is true (going back to company), load company map regardless of other conditions
        if (homeButtonState && (hierarchy == 1 || hierarchy == 2) && (hasGeoJSONFeatures || hasDivisionGeoJson)) {
            mapLoadCompany(map);
        }
        // When subdivision is selected (any hierarchy) and geoJSON has features, load subdivision map with feeder lines
        else if (setSubDivision && hasGeoJSONFeatures) {
            mapLoadSubdivision(map);
        } else if (setSubDivision && hierarchy == 4 && hasDivisionGeoJson) {
            // For hierarchy 4 with subdivision selected but no geoJSON features, use divisionGeoJson
            mapLoadSubdivisionFromDivision(map);
        } else if (hierarchy == 52 && hasGeoJSONFeatures) {
            mapLoadSubdivision(map);
        } else if (setDivision && !setSubDivision && (hierarchy == 1 || hierarchy == 2 || hierarchy == 3)) {
            mapLoadCompany(map);
        } else if (setDivision && hasDivisionGeoJson) {
            mapLoadDivision(map);
        } else if (setCode && !setDivision && (hierarchy == 1 || hierarchy == 2)) {
            mapLoadCompany(map);
        } else if (setCode && !setDivision && hierarchy == 3 && hasDivisionGeoJson) {
            mapLoadCircle(map);
        } else if ((hierarchy == 2 || hierarchy == 1) && hasGeoJSONFeatures && !setDivision && !setSubDivision && !setCode) {
            mapLoadCompany(map);
        } else if (hierarchy == 4 && hasDivisionGeoJson && !setSubDivision) {
            // For hierarchy 4, show division map when no subdivision is selected
            mapLoadDivision(map);
        } else if (hasDivisionGeoJson) {
            mapLoadDivision(map);
        } else {
        }
    }, [geoJSON, setCode, setDivision, setSubDivision, homeButtonState, divisionGeoJson, hierarchy, map, circleCode, divisionCode, selectedCompanyCode]);

    const toggleSwitch = () => {
        if (color) {
            dispatch(updateColor(false));
        } else {
            dispatch(updateColor(true));
        }
    };

    const updateFeederStatus = (geoJSON) => {
        if (geoJSON) {
            const features = geoJSON.features;
            const feederArray = [];

            if (features) {
                const feederFeatures = features.filter((feature) => feature.properties["Feeder Code"] != null && feature.properties["Feeder Code"] != "11kV");
                let feederString = '';
                feederFeatures.forEach((feature) => {
                    const feederCode = feature.properties["Feeder Code"];
                    if (feederCode) {
                        if (!feederArray.includes(feederCode)) {
                            feederString.concat(feederCode.toString()).concat(',');
                            feederArray.push(feederCode);
                        }
                    }
                });

                feederString = feederArray.join(',');
                dispatch(fetchFeederStatus(feederString));
            }
        }
    };

    useEffect(() => {
        updateFeederStatus(geoJSON);
        const intervalId = setInterval(() => {
            updateFeederStatus(geoJSON);
        }, 60000);
        return () => clearInterval(intervalId);
    }, [geoJSON]);

    // onUnmount not needed - cached map persists across mounts

    const handleInfoWindowOpen = () => { };

    const handleKeyPress = (event) => {
        if (event.key === 'Y' || event.key === 'y') {
            if (verificationPopup) {
                handleYes('popup1');
            } else if (verificationPopup1) {
                handleYes('popup2');
            }
        } else if (event.key === 'N' || event.key === 'n') {
            handleNo();
        }
    };

    const handleYes = (popup) => {
        if (popup === 'popup1') {
            setVerificationPopup(false);
            setVerificationPopup1(true);
        } else if (popup === 'popup2') {
            setShowFeedbackDropdown(true);
        }
    };

    const handleNo = () => {
        setVerificationPopup(false);
        setVerificationPopup1(false);
    };

    useEffect(() => {
        if (verificationPopup || verificationPopup1) {
            window.addEventListener('keydown', handleKeyPress);
        } else {
            window.removeEventListener('keydown', handleKeyPress);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [verificationPopup, verificationPopup1]);

    const handleReason = (event) => {
        setReason(event.target.value);
    };

    const handleSubmitTurnOff = async () => {
        const header = `msn=${selectedMarker.msn}&aux=off&remarks=${reason}`;
        const base64Header = btoa(header);
        // Implementation of fetch request remains unchanged
    };

    return isLoaded ? (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div
                ref={mapContainerRef}
                className={isFeederNetworkView() ? 'map-view map-view--feeder-network' : 'map-view'}
                style={{ width: '100%', height: '100%' }}
            />
            <MapContext.Provider value={map}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                        <div className={`switch-container ${color ? 'on' : 'off'}`} style={{ top: 50, left: 30, pointerEvents: 'auto' }} onClick={toggleSwitch}>
                            <div style={{ width: "30px", marginLeft: color ? 10 : 80, marginTop: 3, display: "flex" }}>
                                <p style={{ fontSize: 12, color: 'white', marginTop: 3 }}>{color ? "Light Mode" : "Dark Mode"}</p>
                            </div>
                            <div className="switch-circle"></div>
                        </div>
                        {/* Toggle for Company/Circle/Division levels to switch between Complaints and Lineman */}
                        {(hierarchy == 1 || hierarchy == 2 || hierarchy == 3 || hierarchy == 4) && (
                            <div style={{
                                position: 'absolute',
                                top: 10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: '15px',
                                backgroundColor: 'rgba(0,57,113,0.95)',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                alignItems: 'center',
                                zIndex: 1000,
                                pointerEvents: 'auto',
                            }}>
                                {/* Company Name */}
                                {company && (
                                    <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{company}</span>
                                )}
                                {/* Separator */}
                                {company && <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.3)' }}></div>}
                                {/* Three-way Segmented Toggle with counts */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.25)',
                                    borderRadius: '8px',
                                    padding: '3px',
                                    gap: '3px'
                                }}>
                                    <div
                                        onClick={() => setDisplayMode('lineman')}
                                        style={{
                                            padding: '5px 14px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: displayMode === 'lineman' ? 'rgba(46, 204, 113, 0.9)' : 'transparent',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            border: displayMode === 'lineman' ? 'none' : '1px solid transparent'
                                        }}
                                    >
                                        {/* Signal indicator */}
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: (filteredFieldStaff && filteredFieldStaff.length > 0) ? '#00ff00' : '#ff0000',
                                            boxShadow: (filteredFieldStaff && filteredFieldStaff.length > 0) ? '0 0 6px #00ff00' : '0 0 6px #ff0000'
                                        }}></div>
                                        <span style={{
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>Line Staff</span>
                                        <span style={{
                                            backgroundColor: displayMode === 'lineman' ? 'rgba(255,255,255,0.95)' : 'rgba(46, 204, 113, 0.85)',
                                            color: displayMode === 'lineman' ? '#1e8449' : '#fff',
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            borderRadius: '12px',
                                            fontWeight: '700',
                                            minWidth: '20px',
                                            textAlign: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>{filteredFieldStaff ? filteredFieldStaff.length : 0}</span>
                                    </div>
                                    {/* Designation Dropdown Filter */}
                                    <Select
                                        value={selectedDesignationFilter}
                                        onChange={(e) => setSelectedDesignationFilter(e.target.value)}
                                        size="small"
                                        renderValue={(value) => (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: value === 'all' ? '#2ecc71' :
                                                        value === 'ALM' ? '#e74c3c' :
                                                        value === 'LM-I' ? '#3498db' :
                                                        value === 'LM-II' ? '#9b59b6' :
                                                        value === 'LS-I' ? '#f39c12' : '#1abc9c'
                                                }}></span>
                                                <span>{value === 'all' ? 'All' : value}</span>
                                            </div>
                                        )}
                                        sx={{
                                            minWidth: 95,
                                            height: 30,
                                            backgroundColor: selectedDesignationFilter !== 'all' ? 'rgba(46, 204, 113, 0.15)' : 'rgba(0,0,0,0.25)',
                                            color: 'white',
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            borderRadius: '6px',
                                            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)', fontSize: '18px' },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: selectedDesignationFilter !== 'all' ? 'rgba(46, 204, 113, 0.5)' : 'transparent'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(46, 204, 113, 0.7)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2ecc71' },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: '#1a2942',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    mt: 0.5,
                                                    '& .MuiMenuItem-root': {
                                                        color: 'white',
                                                        fontSize: '12px',
                                                        padding: '8px 12px',
                                                        '&:hover': { backgroundColor: 'rgba(46, 204, 113, 0.15)' },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(46, 204, 113, 0.25)',
                                                            '&:hover': { backgroundColor: 'rgba(46, 204, 113, 0.3)' }
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value="all">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#2ecc71' }}></span>
                                                <span style={{ flex: 1 }}>All</span>
                                                <span style={{
                                                    backgroundColor: 'rgba(46, 204, 113, 0.2)',
                                                    color: '#2ecc71',
                                                    padding: '2px 6px',
                                                    borderRadius: '10px',
                                                    fontSize: '10px',
                                                    fontWeight: 600
                                                }}>{filteredFieldStaff?.length || 0}</span>
                                            </div>
                                        </MenuItem>
                                        <MenuItem value="ALM">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#e74c3c' }}></span>
                                                <span style={{ flex: 1 }}>ALM</span>
                                            </div>
                                        </MenuItem>
                                        <MenuItem value="LM-I">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3498db' }}></span>
                                                <span style={{ flex: 1 }}>LM-I</span>
                                            </div>
                                        </MenuItem>
                                        <MenuItem value="LM-II">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9b59b6' }}></span>
                                                <span style={{ flex: 1 }}>LM-II</span>
                                            </div>
                                        </MenuItem>
                                        <MenuItem value="LS-I">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f39c12' }}></span>
                                                <span style={{ flex: 1 }}>LS-I</span>
                                            </div>
                                        </MenuItem>
                                        <MenuItem value="LS-II">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1abc9c' }}></span>
                                                <span style={{ flex: 1 }}>LS-II</span>
                                            </div>
                                        </MenuItem>
                                    </Select>
                                    <div
                                        onClick={() => setDisplayMode('both')}
                                        style={{
                                            padding: '5px 14px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: displayMode === 'both' ? 'rgba(52, 152, 219, 0.9)' : 'transparent',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {/* Combined signal - green/red split */}
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, ' +
                                                ((filteredFieldStaff && filteredFieldStaff.length > 0) ? '#00ff00' : '#ff0000') + ' 50%, ' +
                                                ((filteredComplaints && filteredComplaints.length > 0) ? '#00ff00' : '#ff0000') + ' 50%)',
                                            boxShadow: '0 0 4px rgba(52, 152, 219, 0.5)'
                                        }}></div>
                                        <span style={{
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>Both</span>
                                    </div>
                                    <div
                                        onClick={() => setDisplayMode('complaints')}
                                        style={{
                                            padding: '5px 14px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: displayMode === 'complaints' ? 'rgba(231, 76, 60, 0.9)' : 'transparent',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {/* Signal indicator */}
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: (filteredComplaints && filteredComplaints.length > 0) ? '#00ff00' : '#ff0000',
                                            boxShadow: (filteredComplaints && filteredComplaints.length > 0) ? '0 0 6px #00ff00' : '0 0 6px #ff0000'
                                        }}></div>
                                        <span style={{
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>Complaints</span>
                                        <span style={{
                                            backgroundColor: displayMode === 'complaints' ? 'rgba(255,255,255,0.95)' : 'rgba(231, 76, 60, 0.85)',
                                            color: displayMode === 'complaints' ? '#922b21' : '#fff',
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            borderRadius: '12px',
                                            fontWeight: '700',
                                            minWidth: '20px',
                                            textAlign: 'center',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>{filteredComplaints ? filteredComplaints.length : 0}</span>
                                    </div>
                                    <div
                                        onClick={() => setDisplayMode('heatmap')}
                                        style={{
                                            padding: '5px 14px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: displayMode === 'heatmap' ? 'rgba(255, 140, 0, 0.9)' : 'transparent',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #00ff00 0%, #ffff00 50%, #ff0000 100%)',
                                            boxShadow: displayMode === 'heatmap' ? '0 0 6px rgba(255,140,0,0.8)' : 'none'
                                        }}></div>
                                        <span style={{
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>Heat Map</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Weather Legend - Show on heatmap mode */}
                        {displayMode === 'heatmap' && (
                            <div style={{
                                position: 'absolute',
                                bottom: '15px',
                                left: '15px',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                zIndex: 10,
                                pointerEvents: 'auto',
                            }}>
                                <div style={{ color: 'white', fontWeight: 'bold', fontSize: '12px', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '5px' }}>
                                    Weather Legend
                                </div>
                                {[
                                    { color: '#ef5350', label: 'Heatwave', desc: '>= 40°C' },
                                    { color: '#ffa726', label: 'Hot', desc: '38°C – 39°C' },
                                    { color: '#81c784', label: 'Mild', desc: '< 38°C' },
                                    { color: '#42a5f5', label: 'Rain / Storm', desc: 'Any temp' },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 3 ? '5px' : 0 }}>
                                        <div style={{
                                            width: '14px',
                                            height: '14px',
                                            borderRadius: '3px',
                                            backgroundColor: item.color,
                                            border: '1px solid rgba(255,255,255,0.4)',
                                            flexShrink: 0,
                                        }}></div>
                                        <span style={{ color: 'white', fontSize: '11px', fontWeight: '600' }}>{item.label}</span>
                                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px' }}>{item.desc}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {selectedMarker && (
                            <InfoWindow
                                position={{
                                    lat: parseFloat(),
                                    lng: parseFloat(),
                                }}
                                style={{ color: 'white', borderRadius: '30px', height: '500px' }}
                                onCloseClick={() => { setSelectedMarker(null); updateSelectedMarker1(); }}
                                onOpen={handleInfoWindowOpen}
                                options={{ disableAutoPan: true }}
                            >
                                <>
                                    {
                                        selectedMarker.apms_capacity ?
                                            <div>
                                                <div style={{ height: 'auto', display: 'flex' }}>
                                                    <div style={{ width: '220px', height: ' increae', backgroundColor: '#F2F3F4', padding: '10px', borderRadius: '5px' }}>
                                                        <p style={{ padding: '5px 0px' }}><span style={{ fontWeight: '800', fontSize: 12 }}>{selectedMarker.name} KVA</span></p>
                                                        {
                                                            selectedMarker.kva < 0 &&
                                                            <p style={{ padding: '5px 0px', color: 'red', textAlign: 'center' }}><span style={{ fontWeight: '800', fontSize: 15, letterSpacing: '2px' }}>NET METER</span></p>
                                                        }
                                                        <div>
                                                            <div
                                                                className={`switch-container1 ${(selectedMarker.contactor_status !== 0) ? 'off' : 'on'}`}
                                                                style={{ left: 30, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                                onClick={() => { if (selectedMarker.contactor_status) { setVerificationPopup(true) } }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: '30px',
                                                                        marginLeft: (selectedMarker.contactor_status !== 0) ? 80 : 10,
                                                                        marginTop: 3,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <p
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: (selectedMarker.contactor_status !== 0) ? 'white' : 'white',
                                                                            fontWeight: 'bold',
                                                                            marginTop: 0,
                                                                            marginBottom: 0
                                                                        }}
                                                                    >
                                                                        {(selectedMarker.contactor_status !== 0) ? 'ON' : 'OFF'}
                                                                    </p>
                                                                </div>
                                                                <div className="switch-circle"></div>
                                                            </div>
                                                            <hr style={{ borderTop: '1px solid gray', marginTop: '10px', width: '100%' }} />
                                                        </div>
                                                        <HighchartsReact highcharts={Highcharts} options={loadOption} />
                                                        <div style={{
                                                            backgroundColor: selectedMarker.kva > 0 ? 'red' : '#E7A350',
                                                            width: '40%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            margin: '0 auto',
                                                            padding: '5px',
                                                            borderRadius: 8,
                                                            marginTop: -30
                                                        }}>
                                                            <p style={{ textAlign: 'center', color: 'white', fontSize: 20 }}>{selectedMarker.kva}</p>
                                                            <p style={{ textAlign: 'center', color: 'white', fontSize: 20 }}>kVA</p>
                                                        </div>
                                                    </div>
                                                    <div style={{ width: '200px', height: 'auto', backgroundColor: '#F2F3F4', padding: '10px', margin: '0px 10px', borderRadius: '5px' }}>
                                                        <HighchartsReact highcharts={Highcharts} options={voltageOptions} />
                                                        <HighchartsReact highcharts={Highcharts} options={currentOptions} />
                                                    </div>
                                                </div>
                                            </div>
                                            :
                                            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', gap: 20, backgroundColor: '#d5e5ff', padding: '10px', borderRadius: '10px' }}>
                                                <div style={{ height: 'auto', }}>
                                                    <p style={{ padding: '5px 0px' }}>Complaint Id: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.ticketNo}</p>
                                                </div>
                                                <div style={{ height: 'auto', }}>
                                                    <p style={{ padding: '5px 0px' }}>Ref No.: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.refNo}</p>
                                                </div>
                                                <div style={{ height: 'auto', }}>
                                                    <p style={{ padding: '5px 0px' }}>Complaint Type: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.complaintType}</p>
                                                </div>
                                                <div style={{ height: 'auto', }}>
                                                    <p style={{ padding: '5px 0px' }}>Registered At: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.registeredAt ? convertDatewithTime(selectedMarker.complaitData.registeredAt) : '-'}</p>
                                                </div>
                                                <div style={{ height: 'auto' }}>
                                                    <p style={{ padding: '5px 0px' }}>Applicant Name: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.name}</p>
                                                </div>
                                                <div style={{ height: 'auto' }}>
                                                    <p style={{ padding: '5px 0px' }}>Address: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.nearByFamousPlace}</p>
                                                </div>
                                                <div style={{ height: 'auto' }}>
                                                    <p style={{ padding: '5px 0px' }}>Phone No.: </p>
                                                    <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.complaitData.mobile}</p>
                                                </div>
                                                {
                                                    selectedMarker.complaitData.status == 'Allocated' &&
                                                    <div style={{ height: 'auto' }}>
                                                        <p style={{ padding: '5px 0px' }}>Assign to:</p>
                                                        <p style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', border: '1px solid grey' }}>{selectedMarker.allocatedTo.userId}</p>
                                                    </div>
                                                }
                                                {
                                                    selectedMarker.complaitData.status == 'Pending' &&
                                                    <DialogActions>
                                                        <Button variant="contained" sx={{ width: '15%' }}
                                                            component="label" type='submit' onClick={handleSubmit}
                                                        >Assign</Button>
                                                    </DialogActions>
                                                }
                                            </div>
                                    }
                                </>
                            </InfoWindow>
                        )}
                        {verificationPopup && (
                            <div className="App">
                                <button>Turn Off APMS</button>
                                <div className="modal-overlay">
                                    <div className="modal-content">
                                        <img src={poweron} />
                                        <h2> Turn Off APMS?</h2>
                                        <p style={{ padding: '5px 0px' }}><span style={{ fontWeight: '800', fontSize: 15 }}>{selectedMarker.name} KVA</span></p>
                                        <p>
                                            Do you want to <strong style={{ fontWeight: 'bold' }}>Turn Off</strong> APMS
                                        </p>
                                        <p>
                                            <strong style={{ fontWeight: 'bold', fontSize: 30, color: 'red' }}>
                                                Y
                                            </strong>
                                            <strong style={{ fontSize: 20, marginLeft: -5 }}>
                                                es
                                            </strong>
                                            <strong style={{ fontSize: 25 }}>
                                                /
                                            </strong>
                                            <strong style={{ fontWeight: 'bold', fontSize: 30, color: 'green' }}>
                                                N
                                            </strong>
                                            <strong style={{ fontSize: 20, marginLeft: 0 }}>
                                                o
                                            </strong>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {verificationPopup1 && (
                            <div className="App">
                                <button>Turn Off APMS</button>
                                <div className="modal-overlay">
                                    <div className="modal-content">
                                        <img src={alert} alt="Alert Icon" />
                                        <h2>Turn Off APMS?</h2>
                                        <p style={{ padding: '5px 0px' }}><span style={{ fontWeight: '800', fontSize: 15 }}>{selectedMarker.name} KVA</span></p>
                                        <p>
                                            Are you Sure to <strong style={{ fontWeight: 'bold' }}>Turn Off</strong> APMS
                                        </p>
                                        <p>
                                            <strong style={{ fontWeight: 'bold', fontSize: 30, color: 'red' }}>
                                                Y
                                            </strong>
                                            <strong style={{ fontSize: 20, marginLeft: -5 }}>
                                                es
                                            </strong>
                                            <strong style={{ fontSize: 25 }}>
                                                /
                                            </strong>
                                            <strong style={{ fontWeight: 'bold', fontSize: 30, color: 'green' }}>
                                                N
                                            </strong>
                                            <strong style={{ fontSize: 20, marginLeft: 0 }}>
                                                o
                                            </strong>
                                        </p>
                                        <>
                                            <p style={{ marginLeft: -250, marginBottom: -2 }}>Reason</p>
                                            <Select
                                                style={{ backgroundColor: '#f3f4f6', padding: '10px', borderRadius: '5px', border: '1px solid grey', height: 40 }}
                                                onChange={handleReason}
                                                displayEmpty
                                                fullWidth
                                                label="Reason"
                                                id="demo-simple-select"
                                                labelId="demo-simple-select-label"
                                            >
                                                <MenuItem value="MT">Maintenance - Work Permit</MenuItem>
                                                <MenuItem value="LL">Load Management</MenuItem>
                                                <MenuItem value="SA">Seasonal</MenuItem>
                                            </Select>
                                        </>
                                        <Button variant="contained" sx={{ width: '100%', marginTop: 2, backgroundColor: 'red' }}
                                            component="label" type='submit' onClick={handleSubmitTurnOff} disabled={reason ? false : true}
                                        >Turn Off</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <Backdrop
                            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                            open={viewBackdrop}
                        >
                            <AppLoader />
                        </Backdrop>
                </div>
            </MapContext.Provider>
        </div>
    ) : <></>;
}

export default React.memo(Map);